import { useState, useEffect } from 'react';

export const useTranslation = () => {
  const [language, setLanguage] = useState('en');

  const t = (key: string) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  return { t, language, setLanguage };
};

export const translations: Record<string, Record<string, string>> = {
  en: {
    navHome: "Home",
    navRooms: "Rooms & Dorms",
    navAbout: "About Us",
    navContact: "Contact",
    btnBook: "Book Now",
    heroRating: "★ 4.9 Superb Boutique Experience",
    heroTitlePart1: "Cozy hostel",
    heroTitlePart2: "in downtown Kyiv",
    heroSubtitle: "Welcome to Hostel Cozzydine, a quiet, safe, and well located accommodation in the city. Featuring comfortable private rooms and shared dorms perfect for solo travelers, couples, remote creators, group explorers, and business guests alike.",
    heroBtnBook: "Book & Save Now",
    heroBtnMap: "Explore Area Guide",
    heroBtnContact: "CONTACT US",
    amenitiesLabel: "Essential comforts",
    amenitiesTitle: "Facilities & Amenities",
    amenitiesDesc: "Curated amenities for guests, featuring essential travel comforts, secure spaces, and high-speed connectivity.",
    amWifi: "Free High-Speed Wi-Fi",
    amSecurity: "CCTV & Security surveillance",
    amAc: "Air Conditioning/Ventilation",
    amKitchen: "Fully Equipped Kitchen",
    amWater: "Consistent Hot Water Supply",
    amLuggage: "Luggage Storage",
    amLockers: "Secure Lockers",
    amLaundry: "Self-Service Laundry",
    amReception: "24 Hour Front Desk",
    promoTag: "HERO INITIATIVE",
    promoTitle: "Explore Ukraine's Radiant Heritage",
    promoDesc: "Kyiv is an incredibly dynamic capital of art, culture, and resilience. Rest easy at Cozzydine with our secure basement bunker features, highly responsive host support, and close-knit transit links.",
    promoBtn: "Secure Bed & Reserve",
    aboutTitlePart1: "Bespoke Hospitality",
    aboutTitlePart2: "Since 2020",
    aboutQuarterTitle: "KYIV COZZYDINE HQ",
    aboutQuarterDesc: "Managed directly by Chinju Ravi and the Kyiv hospitality teams with multi-lingual assistance.",
    contactTag: "ALWAYS ON SITE",
    contactTitle: "Contact Cozzydine",
    contactSubtitle: "Contact us for custom booking requirements, arrival support, or location guidance.",
    contactFormTitle: "Message Us Directly",
    contactSubmittedTitle: "Message Delivered Safely",
    contactSubmittedDesc: "Thank you! Cozzydine staff or Cozy AI Host will verify your query and follow up within 15 minutes.",
    contactLblName: "Your Full Name",
    contactLblEmail: "Email Address",
    contactLblMsg: "Your Message or Inquiry",
    contactBtnSubmit: "Send Message Now",
    
    roomType: "Room Type",
    // Additional standard baseline keys
    home: "Home",
    rooms: "Rooms & Dorms",
    about: "About Us",
    contact: "Contact",
    guestName: "Guest Name",
    guestEmail: "Guest Email",
    checkIn: "Check-In",
    checkOut: "Check-Out",
    guests: "Guests",
    submit: "Submit",
    successMessage: "Successfully submitted!",
    close: "Close",
    open: "Open",
    learnMore: "Learn More",
    backToStart: "Back to Start",
    policiesTitle: "Policies",
    policyCatAll: "All Policies",
    policyCatStay: "Arrival & Stay",
    policyCatFacilities: "Facilities & Amenities",
    policyCatRules: "House Rules",
    policiesSubtitle: "To ensure a peaceful, safe, and highly comfortable stay for all of our guests, please review our core operational rules and standards.",
    policyCheckInOutTitle: "Check-In / Out",
    policyCheckInOutDesc: "Check-in: 14:00 - 23:00. Check-out: before 11:00. Early check-in and late check-out are subject to availability, prior notification and approval.",
    policyQuietTitle: "Quiet Hours",
    policyQuietDesc: "Quiet hours apply in all rooms and common areas from 23:00 to 08:00.",
    policyPaymentTitle: "ID, Payment & Age Policy",
    policyPaymentDesc: "We prefer card payments, and a valid government-issued photo ID or passport is required for check-in. Please note that a minimum age requirement of 18 applies to all dormitory accommodations.",
    policyCancellationTitle: "Cancellation Policy",
    policyCancellationDesc: "• Standard Rates: Cancellations or modifications are free of charge via prior notification up to 48 hours before arrival; late changes incur cancellation fee equivalent of one night.\n\n• Non-Refundable Rates: The total booking amount is charged upon reservation and is strictly non-refundable and non-modifiable.\n\n• No-Shows: Missing a reservation without prior notification results in immediate cancellation and a charge for the full stay.",
    policyLuggageTitle: "Luggage Storage",
    policyLuggageDesc: "We offer free luggage storage for same-day collection, available both before check-in and after check-out. Please request this service in advance or inform reception before leaving your bags.",
    policyLaundryTitle: "Laundry",
    policyLaundryDesc: "On site self service facilities available. Cost per wash is 100 UAH, check with the reception and confirm availability. Do not hang wet laundry or towels in the dorms, use the designated drying room.",
    policyComfortTitle: "Alcohol, Drugs & Smoking",
    policyComfortDesc: "The possession, distribution, or consumption of alcohol and illegal substances is strictly prohibited; smoking and vaping are entirely forbidden in the hostel.",
    policyShelterTitle: "Safety & Air Shelter",
    policyShelterDesc: "The hostel is located on the ground floor and semibasement floor, with a metro station conveniently nearby.",
    policyVisitorsTitle: "No Outside Visitors",
    policyVisitorsDesc: "Access to the hostel premises is restricted entirely to registered guests with a confirmed and active booking. Guests are permitted on-site only after check-in; unregistered outside visitors are strictly prohibited.",
    policyFoodTitle: "Dorm Etiquette",
    policyFoodDesc: "Eating or storing open food and drinks in dorms is prohibited to maintain cleanliness and prevent pests. Keep your luggage and personal belongings inside your designated locker or under your bed. Do not spread your items across the floor, walkways, or onto unassigned beds.",
    policyKitchenTitle: "Toilette & Kitchnette",
    policyKitchenDesc: "To ensure a clean and comfortable environment for all guests, we require adherence to the following operational rules:\n\nKitchenette: Wash, dry, and put away all kitchenware immediately after use to keep the counters clear. Ensure the dining table is wiped clean after your meal.\n\nBathrooms: Flush the toilet after use, wipe down countertops, and use the provided mop to leave the floor clean and dry before exiting.",
    policyLockersTitle: "Secure Lockers",
    policyLockersDesc: "Standalone lockers are available in hostel dorms for securing personal belongings.",
    policyWorkspaceTitle: "Free High-Speed Wi-Fi",
    policyWorkspaceDesc: "Yes, free high-speed Wi-Fi is available throughout the hostel.",
    exploreRooms: "Explore Room Suites",
    galleryLabel: "Vibe Tour",
    galleryTitle: "Gallery",
    gallerySubtitle: "Take a visual journey through Cozzydine's cozy corners, premium social spaces, and quiet dorms.",
    galleryAlt_1: "Double room, Hostel Cozzydine, Kyiv",
    galleryAlt_2: "Corridor and interior, Hostel Cozzydine, Kyiv",
    galleryAlt_3: "Reception desk and Lobby, Hostel Cozzydine, Kyiv",
    galleryAlt_4: "Private double bedroom, Hostel Cozzydine, Kyiv",
    galleryAlt_5: "Double bedroom, Hostel Cozzydine, Kyiv",
    galleryAlt_6: "4 bed dormitory room, Hostel Cozzydine, Kyiv",
    galleryAlt_7: "6 bed dormitory room, Hostel Cozzydine, Kyiv",
    stampGalleryBadge: "Kyiv Philatelic Edition",
    lightboxPostage: "Kyiv Archival Collection",
    slide1Title: "Cozy Shared Lounge",
    slide1Desc: "The perfect place to work, read, play games, or connect with international travelers.",
    slide2Title: "Comfortable Dorm Beds",
    slide2Desc: "Orthopaedic mattresses, direct power plugs, reading lights, and heavy blackout curtains for deep rest.",
    slide3Title: "Cozy Purple Floral Room",
    slide3Desc: "A bright and tidy double room featuring signature lavender-patterned bedding, an elegant wall-mounted mirror, a clothes hanger, and a cozy chest of drawers.",
    slide4Title: "Social Kitchenette",
    slide4Desc: "Equipped with microwave, kettle, toaster, and dining utensils. Grab a bite and make new friends.",
    slide5Title: "Premium Twin Room",
    slide5Desc: "A peaceful private sanctuary with luxury linen, workspace table, and premium decor.",
    slide6Title: "Charming Courtyard Entrance",
    slide6Desc: "Our quiet courtyard patio painted in signature black and white, steps away from Lybidska.",
    filterAll: "All",
    filterPrivate: "Private",
    filterDorms: "Dorms",
    directionsTitle: "Directions & Transit Guide",
    directionsSubtitle: "How to reach Hostel Cozzydine",
    transitLybidskaLabel: "Lybidska Metro Exit 2 (< 2 mins)",
    transitPalatsLabel: "Kyiv City Centre (10 mins)",
    transitTrainLabel: "Central Railway (10 mins)",
    monumentLabel: "🏛️ Independence Monument, Kyiv",
    roomsAndDorms: "Rooms & Dorms",
    aerialLabel: "🌉 Downtown Kyiv Aerial Vista",
    step1of3: "Step 1 of 3",
    step2of3: "Step 2 of 3",
    step3of3: "Step 3 of 3",
    lblGuestFullName: "Guest Full Name",
    lblGuestEmailAddress: "Email address",
    lblGuestNumber: "Number of Guests",
    lblGuestSingular: "Guest",
    lblGuestPlural: "Guests",
    lblDailyRate: "Daily Rate:",
    lblSelectedRange: "Selected Range:",
    lblEstimatedTotal: "Estimated Total:",
    lblNights: "nights",
    lblSelected: "Selected",
    lblInRange: "In Range",
    lblSelectionPreview: "Preview",
    lblBooked: "Booked",
    widgetTitle: "Website Language",
    translatorTitle: "Website Translation",
    changeAppLang: "Change App Language",
    placeholderTranslate: "Type a phrase to translate...",
    translationResult: "Translation Result",
    copy: "Copy",
    copied: "Copied",
    translatingProgress: "Generating perfect translation...",
    btnTranslate: "Translate",
    btnTranslating: "Translating...",
    phrasebook: "Hostel Phrasebook",
    from: "From",
    to: "To",
    lblChooseCheckIn: "Please interact with the visual calendar grid to choose your Check-In date.",
    lblChooseCheckOut: "Excellent check-in! Now, click on your check-out date.",
    lblPaymentMethod: "Select Payment Method",
    payOnlineLiqPay: "Secure Online LiqPay",
    payOnlineDetails: "Instant guarantee • Card / Privat24",
    payOnlineDetailsForm: "Instant guarantee • Visa / Mastercard / Privat24",
    payAtReception: "Pay at Desk Reception",
    payAtReceptionDetails: "Pay cash or card at desk upon arrival in Kyiv",
    payAtReceptionDetailsForm: "No credit card pre-auth • Pay cash or card at desk",
    btnConfirmReservation: "Confirm Reservation",
    btnProceedToCheckout: "Proceed to Checkout",
    lblContactPhone: "Contact Phone Number",
    lblPhonePlaceholder: "e.g. 50 123 4567",
    lblSecureHotelReservation: "Secure Hotel Reservation",
    lblCheckoutReady: "Checkout Ready",
    lblReservationConfirmedHeader: "RESERVATION CONFIRMED",
    lblCongratulations: "Congratulations",
    lblReservedSafely: "is reserved safely.",
    lblConfirmationId: "Confirmation ID:",
    lblCheckInDate: "Check-In date:",
    lblCheckOutDate: "Check-Out date:",
    lblPaidTransaction: "Paid Transaction:",
    lblTotalSettled: "Total Settled:",
    lblReceptionTip: "Present either this Confirmation ID or paid transaction clip at reception desks upon arrival! Lobby is 24/7.",
    lblReserveRoom: "Reserve",
    lblSecureCheckout: "Secure Checkout",
    lblReceiptVoucher: "Receipt Voucher",
    lblGuestRepresentative: "Guest Representative:",
    lblReservedRoom: "Reserved Room:",
    lblCheckInIntake: "Check-In Intake:",
    lblCheckOutDeparture: "Check-Out Departure:",
    lblAuthorizedGuests: "Authorized Guests:",
    lblGatewayTransactionRef: "Gateway Transaction Ref:",
    lblReceptionTipModal: "Present either your confirmation voucher ID or reservation name at the hostel lobby upon check-in. The reception is staffed 24/7.",
    lblShowLess: "show less",
    lblMore: "more",
    lblCapacity: "Capacity",
    lblBedsLeft: "beds left",
    lblBedLeft: "bed left",
    bookingConfirmedTitle: "Booking Approved & Confirmed",
    bookingConfirmedDesc: "Congratulations! Your transaction is secured and verified via LiqPay. Your boutique accommodation in Kyiv is fully locked in.",
    btnReturnExplore: "Return to Explore",
    aboutText: "Cozzydine is a Ukrainian company established in 2020, specializing in the vital lodging sector of the hospitality industry, dedicated to providing quality temporary accommodations that seamlessly serve both domestic and international travelers.\n\nHostel Cozzydine is our new venture aimed at providing affordable accommodation for a wide range of guests—such as backpackers, hitchhikers, couples, remote creators, urban migrants, group explorers, and business travelers alike.\n\nLocated in downtown Kyiv, our property is meticulously designed to offer a safe and secure, well-connected accommodation equipped with all the essential amenities for a comfortable stay, restorative rest, and a curated connection to Kyiv’s inspiring arts and cultural resilience hubs.",
    sightseeingTag: "Interactive Sightseeing Guide",
    sightseeingTitle: "Kyiv's Arts, Culture & Resilience Hubs",
    sightseeingDesc: "Hostel Cozzydine benefits from direct walking proximity to Metro links connecting you straight into downtown Kyiv. Explore some local landmarks:",
    sight1Name: "Khreshchatyk & Maidan",
    sight1Desc: "The historic city square lined with outdoor cafes, monumental plazas and vibrant street artists.",
    sight1Meters: "9 mins via Metro",
    sight2Name: "Kyiv Pechersk Lavra",
    sight2Desc: "Dazzling centuries-old gold domes, lush monastery forests and sacred underground passage caves.",
    sight2Meters: "10 mins ride",
    sight3Name: "Saint Sophia Cathedral & Golden Gate",
    sight3Desc: "A breathtaking 11th-century UNESCO heritage cathedral paired with Kyiv's ancient medieval gateway ruins.",
    sight3Meters: "12 mins via Metro",
    sight4Name: "Andriyivskyy Descent & Podil",
    sight4Desc: "Kyiv's bohemian art street filled with galleries, craft handiwork markets, and cozy third-wave cafes.",
    sight4Meters: "15 mins via Metro",
    sight5Name: "St. Nicholas Roman Catholic Church",
    sight5Desc: "Stunning Neo-Gothic architectural masterpiece designed by Vladyslav Gorodetsky, active Organ Music Hall.",
    sight5Meters: "4 mins ride",
    sight6Name: "Motherland Monument (Rodyna-Mat)",
    sight6Desc: "Ukraine's tallest monumental steel statue overlooking the Dnipro River, symbolizing resilience and national pride.",
    sight6Meters: "10 mins ride",
    sight7Name: "Volodymyrskyy Hill & Park",
    sight7Desc: "A peaceful hilltop park overlooking the Dnipro River, featuring the landmark monument and scenic pedestrian bridge.",
    sight7Meters: "12 mins ride",
    checklistTitle: "Backpacker Transit Checklist",
    checklistSpot2: "Lybidska Subway station",
    checklistSpotBus: "Lybidska Bus station",
    checklistSpotCentralBus: "Kyiv Central Bus Station",
    checklistSpotRailway: "Kyiv Central Railway Station",
    checklistSpot3: "Kyiv City Centre",
    checklistWalkTip: "Walk right into courtesy backyard 75a, door bells clearly marked Cozzydine!",
    physicalEndpointLabel: "PHYSICAL ENDPOINT & CONTACTS",
    directChatsLabel: "DIRECT CHATS & SOCIALS",
    workingHoursLabel: "Working Hours:",
    workingHoursValue: "24/7 (Always Open)",

    // Room Details
    room_type_private: "Private Room",
    room_type_dorm: "Shared Dorm",
    lblLongStayOfferTitle: "Long-Stay Offer",
    lblLongStayOfferDesc: "Book 3+ nights for a 10% discount, or 7+ nights for 15% off.",
    "room_double-room_name": "Double Room",
    "room_double-room_desc": "Comfortable private room featuring one large bed, perfect for two guests. Includes free Wi-Fi, fresh linens, and standard amenities for a relaxing stay.",
    "room_eight-bed-dorm_name": "8-Bed Dorm",
    "room_eight-bed-dorm_desc": "Budget-friendly shared room featuring eight comfortable bunk beds. Includes free WiFi, personal secure lockers, power outlets, access to shared bathroom & kitchenette.",
    "room_six-bed-dorm_name": "6-Bed Dorm",
    "room_six-bed-dorm_desc": "Cozy shared room featuring six comfortable bunk beds. Includes free WiFi, personal secure lockers, power outlets, access to shared bathroom & kitchenette.",
    "room_four-bed-womens-dorm_name": "4-Bed Women's Dorm",
    "room_four-bed-womens-dorm_desc": "Cozy all-female shared room featuring four comfortable bunk beds. Includes free WiFi, personal secure lockers, power outlets, access to shared bathroom & kitchenette.",
    "room_four-bed-mens-dorm_name": "4-Bed Men's Dorm",
    "room_four-bed-mens-dorm_desc": "Cozy all-male shared room featuring four comfortable bunk beds. Includes free WiFi, personal secure lockers, power outlets, access to shared bathroom & kitchenette.",

    // Interactive Transit & Local guide tabs
    tabLybidska: "Lybidska Metro Exit 2 (< 2 mins)",
    tabBus: "Lybidska Bus Stop (< 2 mins)",
    tabPalats: "Kyiv City Centre (Maidan Nezalezhnosti)",
    tabTrain: "Central Railway (10 mins)",
    tabCentralBus: "Central Bus Station (3 mins)",

    // Metro Access & Transit Box
    metroAccessTitle: "🚇 Metro Access & Transit",
    metroAccessDesc: "Hostel Cozzydine Kyiv is incredibly easy to reach via public transit. We are just a brief stroll from major metropolitan lines:",
    metroAccessItem1: "Lybidska Metro Station (Exit 2) — less than 2 mins walk",
    metroAccessItem2: "Kyiv City Centre (Maidan Nezalezhnosti Subway Station) — 10 mins ride (5.3 km)",
    metroAccessItem3: "Kyiv Passenger Central — 10 mins metro ride",

    // Rooms Subtitle
    roomsSubtitle: "Comfortable private rooms and social shared dorms. Find your perfect space, enjoy premium amenities, and connect seamlessly.",

    // Testimonials
    testimonialsTag: "💬 Guest Testimonials",
    testimonialsTitle: "Guest Experiences",
    testimonialsDesc: "Real authenticated feedback and stories from our backpackers, group travelers, business travelers and digital nomads.",
    reviewsTitle: "What Our Guests Say",

    // Guest Review Translations
    "rev_REV-01_content": "Great location, right next to metro and ocean plaza shopping centre. Neat and tidy and the owner is always on site 24/7 if you have queries or issues(which you won't). If you're looking for affordable accommodation in the centre of Kyiv I'd highly recommend hostel cozzydine. I will definitely be staying here again during my time in Ukraine.",
    "rev_REV-01_tag1": "Near Metro",
    "rev_REV-01_tag2": "Ocean Plaza",
    "rev_REV-01_tag3": "Affordable Centre",
    "rev_REV-01_tag4": "24/7 Support",
    "rev_REV-02_content": "Hostel is good for the price, everything is clean, beds are very comfortable. And the host is friendly and helpful. I recommend!",
    "rev_REV-02_tag1": "Great Price",
    "rev_REV-02_tag2": "Super Clean",
    "rev_REV-02_tag3": "Comfortable Beds",
    "rev_REV-02_tag4": "Friendly Host",
    "rev_REV-03_content": "The host is very conscientious about keeping the place tidy and is very responsive.",
    "rev_REV-03_tag1": "Conscientious Host",
    "rev_REV-03_tag2": "Always Tidy",
    "rev_REV-03_tag3": "Highly Responsive",
    "rev_REV-04_content": "Loved the communication with me as a non-local traveler. They were constantly in touch and explained exactly how to get there. The hostel is clean and neat, with a comfortable bed and fresh linen. There is a shower and a kitchen. Since it is located in a basement facility, you can safely wait out air raid alarms here.",
    "rev_REV-04_tag1": "Great Communication",
    "rev_REV-04_tag2": "Clean & Tidy",
    "rev_REV-04_tag3": "Safe Shelter",
    "rev_REV-04_tag4": "Comfortable Bed",
    "rev_REV-05_content": "I have been here for the second time and it was a great pleasure to stay here again. Chinju is wonderful and professional host. I had some flight issues and arrived really late, but late check-in was no problem. It was really easy and flexible. The room was again very clean and well equipped with all the needful things. The view from the balcony is still outstanding. It’s super located, if you are interested in art and culture. Even the best techno (music) clubs are not far away. I highly recommend this place and would stay there anytime again.",
    "rev_REV-05_tag1": "Repeat Guest",
    "rev_REV-05_tag2": "Professional Host",
    "rev_REV-05_tag3": "Outstanding Balcony View",
    "rev_REV-05_tag4": "Cultural Hub Location",
    "rev_REV-06_content": "Loved this place! Mostly because of the excellent location, wonderful view and perfect price for this city! It's not posh, but it's elegant and simple. I booked for two nights and stayed for two weeks. I did not want to leave my great room here, you'll feel the same if you like space, simple touches and great views. I can't recommend it highly enough.",
    "rev_REV-06_tag1": "Extended Stay",
    "rev_REV-06_tag2": "Wonderful View",
    "rev_REV-06_tag3": "Elegant & Simple",
    "rev_REV-06_tag4": "Unbeatable Price",
    "rev_REV-07_content": "Hotel Cozzyden was the perfect place for us to calm down, unwind and relax after a stressful flight experience led to an unexpected one night stay in Kyiv. We had wonderful views over the city, building is charming, apartment was immaculate, plenty of space and the bathroom is particularly large and lovely. Our host Chinju was really great to us. Would absolutely return to Kyiv and stay here again.",
    "rev_REV-07_tag1": "Perfect Sanctuary",
    "rev_REV-07_tag2": "Immaculate Apartment",
    "rev_REV-07_tag3": "Large Lovely Bathroom",
    "rev_REV-07_tag4": "Superb Views",
    "rev_REV-08_content": "It was like a fairytale staying in the beautiful Hotel Cozzydine. Thank you Chinju for being available 24/7 and for being such a good host. Would definitely stay here again in the future!",
    "rev_REV-08_tag1": "Fairytale Vibe",
    "rev_REV-08_tag2": "24/7 Availability",
    "rev_REV-08_tag3": "Outstanding Host",
    "rev_REV-08_tag4": "Beautiful Decor",
    perNight: "/ night",
    bedsAvailable: "beds available",
    bedAvailable: "bed available",
    longStayDiscount: "Long-Stay Discount Applied!",
    lblPrivacyPolicy: "Privacy Policy",
    lblAllRightsReserved: "All rights reserved.",
    lblPreparingBookingEngine: "Preparing booking engine...",
    lblLoadingMap: "Loading Cozzy Business Location Map...",
    lblEmail: "Email:",
    lblCallFrontDesk: "Call (Front Desk):",
    lblHostel: "Hostel",
    lblTransit250m: "250 meters",
    lblTransit300m: "300 meters",
    lblTransit3m: "3 mins ride",
    lblTransit10m: "10 mins ride",
    lblCozzyAiAssistant: "Cozy AI",
    lblCozzyCoreAi: "Cozy Core AI"
  },
  uk: {
    navHome: "Головна",
    navRooms: "Номери та спальні капсули",
    navAbout: "Про нас",
    navContact: "Контакти",
    btnBook: "Забронювати",
    heroRating: "★ 4.9 Чудовий Бутик-Хостел",
    heroTitlePart1: "Затишний хостел",
    heroTitlePart2: "у центрі Києва",
    heroSubtitle: "Ласкаво просимо до хостелу Cozzydine — тихого, безпечного та зручно розташованого житла в місті. Пропонуючи комфортні окремі номери та загальні спальні кімнати, що ідеально підходять для соло-мандрівників, пар, цифрових кочівників, груп та бізнес-гостей.",
    heroBtnBook: "Забронювати зараз",
    heroBtnMap: "Переглянути карту",
    heroBtnContact: "Зв'язатися з нами",
    amenitiesLabel: "ПРЕМІУМ ПОСЛУГИ",
    amenitiesTitle: "Зручності та послуги",
    amenitiesDesc: "Старанно підібрані зручності, які пропонують необхідні туристичні вигоди, безпечний житловий простір та зони швидкісного інтернету.",
    amWifi: "Безкоштовний швидкісний Wi-Fi",
    amSecurity: "Відеоспостереження CCTV та охорона",
    amAc: "Кондиціонер та вентиляція",
    amKitchen: "Повністю обладнана кухня",
    amWater: "Постійне постачання гарячої води",
    amLuggage: "Зберігання багажу",
    amLockers: "Надійні локери",
    amLaundry: "Пральня самообслуговування",
    amReception: "Цілодобова стійка реєстрації",
    promoTag: "БЕЗПЕКА ТА ПІДТРИМКА",
    promoTitle: "Відкрийте для себе величний Київ",
    promoDesc: "Київ — неймовірна та сильна культурна столиця. Почувайтеся у цілковитій безпеці в нашому чистому хостелі з надійним бомбосховищем, гарячим душем та відмінною комунікацією.",
    promoBtn: "Забронювати ліжко",
    aboutTitlePart1: "Комфорт та безпека",
    aboutTitlePart2: "З 2020 року",
    aboutQuarterTitle: "Cozzydine Гостинність",
    aboutQuarterDesc: "Безпосереднє управління від Chinju Ravi та київської команди з цілодобовою турботою.",
    contactTag: "ЗАВЖДИ НА ЗВ'ЯЗКУ",
    contactTitle: "Зв'язатися з Cozzydine",
    contactSubtitle: "Зв'яжіться з нами для індивідуальних умов бронювання, підтримки при прибутті або орієнтування на місцевості.",
    contactFormTitle: "Напишіть нам напряму",
    contactSubmittedTitle: "Повідомлення успішно надіслано",
    contactSubmittedDesc: "Дякуємо! Персонал Cozzydine або віртуальний хост Cozzy дадуть відповідь протягом 15 хвилин.",
    contactLblName: "Ваше повне ім'я",
    contactLblEmail: "Адреса електронної пошти",
    contactLblMsg: "Ваше повідомлення або запит",
    contactBtnSubmit: "Надіслати повідомлення",

    roomType: "Тип кімнати",
    // Additional standard baseline keys
    home: "Головна",
    rooms: "Номери та спальні капсули",
    about: "Про нас",
    contact: "Контакти",
    guestName: "Ім'я гостя",
    guestEmail: "Електронна пошта",
    checkIn: "Дата заїзду",
    checkOut: "Дата виїзду",
    guests: "Кількість гостей",
    submit: "Надіслати",
    successMessage: "Успішно надіслано!",
    close: "Закрити",
    open: "Відкрити",
    learnMore: "Детальніше",
    backToStart: "На початок",
    policiesTitle: "Правила та регламент хостелу",
    policyCatAll: "Всі правила",
    policyCatStay: "Заїзд та оплата",
    policyCatFacilities: "Зручності та зони",
    policyCatRules: "Правила дому",
    policiesSubtitle: "Для забезпечення спокійного, безпечного та максимально затишного перебування всіх гостей, будь ласка, ознайомтеся з нашими стандартами та правилами дому.",
    policyCheckInOutTitle: "Заселення та виїзд",
    policyCheckInOutDesc: "Заселення: 14:00-23:00. Виселення: до 11:00. Раннє заселення та пізній виїзд можливі за наявності вільних місць, за умови попереднього повідомлення та погодження.",
    policyQuietTitle: "Режим тиші",
    policyQuietDesc: "Режим тиші діє у всіх кімнатах та місцях загального користування з 23:00 до 08:00.",
    policyPaymentTitle: "Особисті документи, оплата та вікові правила",
    policyPaymentDesc: "Ми надаємо перевагу оплаті карткою, а для заселення необхідне дійсне посвідчення особи державного зразка або паспорт. Будь ласка, зверніть увагу, що мінімальне вікове обмеження у 18 років діє для всіх загальних спальних кімнат.",
    policyCancellationTitle: "Правила скасування",
    policyCancellationDesc: "• Стандартні тарифи: скасування або зміни є безкоштовними за умови попереднього повідомлення не пізніше ніж за 48 годин до прибуття; пізні зміни тягнуть за собою стягнення штрафу у розмірі вартості однієї ночі.\n\n• Тарифи без повернення коштів: повна вартість проживання стягується в момент бронювання, а саме бронювання не підлягає поверненню коштів або змінам.\n\n• Неявка: неприбуття без попереднього повідомлення призводить до негайного скасування бронювання та стягнення плати за весь період проживання.",
    policyLuggageTitle: "Зберігання багажу",
    policyLuggageDesc: "Ми пропонуємо безкоштовне зберігання багажу для отримання в той самий день, доступне як перед заїздом, так і після виїзду. Будь ласка, замовте цю послугу заздалегідь або повідомте адміністрацію перед тим, як залишити ваші речі.",
    policyLaundryTitle: "Прання",
    policyLaundryDesc: "Доступні послуги самообслуговування на місці. Вартість прання становить 100 UAH, узгоджуйте на рецепції та уточнюйте наявність. Не розвішуйте мокру білизну або рушники в спальних кімнатах, користуйтеся спеціально обладнаною сушильнею.",
    policyComfortTitle: "Алкоголь, наркотики та куріння",
    policyComfortDesc: "Зберігання, розповсюдження або вживання алкоголю та незаконних речовин суворо заборонено; куріння та вейпінг у хостелі повністю заборонені.",
    policyShelterTitle: "Безпека та укриття",
    policyShelterDesc: "Хостел розташований на першому та підвальному поверхах, поруч також є станція метро.",
    policyVisitorsTitle: "Без сторонніх",
    policyVisitorsDesc: "Вхід стороннім особам без бронювання суворо заборонено.",
    policyFoodTitle: "Етикет у спальних кімнатах",
    policyFoodDesc: "Вживання та зберігання їжі або відкритих напоїв у спальних кімнатах заборонено для підтримки чистоти та запобігання появі комах. Зберігайте свій багаж та особисті речі у спеціально відведеній шафці або під ліжком. Не розкладайте свої речі на підлозі, у проходах або на незайнятих ліжках.",
    policyKitchenTitle: "Туалет та міні-кухня",
    policyKitchenDesc: "Мийте та ховайте весь кухонний посуд одразу після використання. Завжди змивайте воду в туалеті, протирайте поверхні та мийте підлогу перед виходом.",
    policyLockersTitle: "Особисті сейфи",
    policyLockersDesc: "Окремі шафки доступні в загальних номерах хостелу для безпечного зберігання особистих речей.",
    policyWorkspaceTitle: "Безкоштовний швидкісний Wi-Fi",
    policyWorkspaceDesc: "Так, безкоштовний швидкісний Wi-Fi доступний на всій території хостелу.",
    exploreRooms: "Фільтр кімнат",
    galleryLabel: "Візуальний тур",
    galleryTitle: "Галерея хостелу",
    gallerySubtitle: "Здійсніть візуальну подорож затишними куточками Cozzydine, преміальними зонами відпочинку та тихими кімнатами.",
    galleryAlt_1: "Двомісний номер, Хостел Коззідайн, Київ",
    galleryAlt_2: "Коридор та інтер'єр, Хостел Коззідайн, Київ",
    galleryAlt_3: "Стійка рецепції та лобі, Хостел Коззідайн, Київ",
    galleryAlt_4: "Окрема двомісна спальня, Хостел Коззідайн, Київ",
    galleryAlt_5: "Двомісна спальня, Хостел Коззідайн, Київ",
    galleryAlt_6: "4-місний загальний номер, Хостел Коззідайн, Київ",
    galleryAlt_7: "6-місний загальний номер, Хостел Коззідайн, Київ",
    stampGalleryBadge: "Київське філателістичне видання",
    lightboxPostage: "Київська архівна колекція",
    slide1Title: "Затишна вітальня",
    slide1Desc: "Ідеальне місце для роботи, читання, настільних ігор чи спілкування з іноземними мандрівниками.",
    slide2Title: "Комфортні спальні місця",
    slide2Desc: "Ортопедичні матраци, розетки, світильники для читання та щільні штори для глибокого сну.",
    slide3Title: "Затишна фіолетова кімната",
    slide3Desc: "Світла та охайна двоспальна кімната з фірмовою білизною у фіолетовий квітковий візерунок, настінним дзеркалом, вішалкою та комодом.",
    slide4Title: "Кухонна зона (міні-кухня)",
    slide4Desc: "Обладнана мікрохвильовкою, чайником, тостером та столовим приладдям. Перекусіть та знайдіть друзів.",
    slide5Title: "Преміум Твін кімната",
    slide5Desc: "Тихий приватний куточок з розкішною білизною, робочим столом і сучасним декором.",
    slide6Title: "Чарівний вхід з дворика",
    slide6Desc: "Наш затишний внутрішній двір, оформлений у чорно-білих тонах, за два кроки від Либідської.",
    filterAll: "Всі",
    filterPrivate: "Приватні",
    filterDorms: "Загальні",
    directionsTitle: "Маршрути та проїзд",
    directionsSubtitle: "Як легко дістатися до хостелу Cozzydine від вокзалів та ліній метро.",
    transitLybidskaLabel: "Метро Либідська, Вихід 2 (< 2 хв)",
    transitBus: "Автобусна зупинка (< 2 хв)",
    transitPalatsLabel: "Київський центр (Майдан Незалежності)",
    transitTrainLabel: "Центральний вокзал (10 хв)",
    monumentLabel: "🏛️ Монумент Незалежності, Київ",
    roomsAndDorms: "Номери та капсули",
    aerialLabel: "🌉 Аеровід на центр Києва",
    step1of3: "Крок 1 з 3",
    step2of3: "Крок 2 з 3",
    step3of3: "Крок 3 з 3",
    lblGuestFullName: "Повне ім'я гостя",
    lblGuestEmailAddress: "Адреса електронної пошти",
    lblGuestNumber: "Кількість гостей",
    lblGuestSingular: "Гість",
    lblGuestPlural: "Гості",
    lblDailyRate: "Ціна за добу:",
    lblSelectedRange: "Період:",
    lblEstimatedTotal: "Разом:",
    lblNights: "ночей",
    lblSelected: "Вибрано",
    lblInRange: "В діапазоні",
    lblSelectionPreview: "Попередній перегляд",
    lblBooked: "Зайнято",
    widgetTitle: "Мова сайту",
    translatorTitle: "Переклад сайту",
    changeAppLang: "Змінити мову додатка",
    placeholderTranslate: "Введіть фразу для перекладу...",
    translationResult: "Результат перекладу",
    copy: "Копіювати",
    copied: "Скопійовано",
    translatingProgress: "Генерація ідеального перекладу...",
    btnTranslate: "Перекласти",
    btnTranslating: "Переклад...",
    phrasebook: "Розмовник хостелу",
    from: "З",
    to: "До",
    lblChooseCheckIn: "Будь ласка, оберіть дату заїзду на інтерактивному календарі.",
    lblChooseCheckOut: "Тепер оберіть дату виїзду на календарі.",
    lblPaymentMethod: "Спосіб оплати",
    payOnlineLiqPay: "Онлайн оплата LiqPay",
    payOnlineDetails: "Миттєво • Картка / Privat24",
    payOnlineDetailsForm: "Миттєве підтвердження • Без націнок та комісій • Всі картки",
    payAtReception: "Оплата при заїзді",
    payAtReceptionDetails: "Готівка чи картка в хостелі при заїзді",
    payAtReceptionDetailsForm: "Сплачуйте готівкою або карткою при заїзді на рецепції",
    btnConfirmReservation: "Підтвердити бронь",
    btnProceedToCheckout: "Перейти до оплати",
    lblContactPhone: "Контактний номер телефону",
    lblPhonePlaceholder: "наприклад, 50 123 4567",
    lblSecureHotelReservation: "Безпечне бронювання номеру",
    lblCheckoutReady: "Готово до оформлення",
    lblReservationConfirmedHeader: "БРОНЮВАННЯ ПІДТВЕРДЖЕНО",
    lblCongratulations: "Вітаємо",
    lblReservedSafely: "успішно зарезервовано.",
    lblConfirmationId: "ID підтвердження:",
    lblCheckInDate: "Дата заїзду:",
    lblCheckOutDate: "Дата виїзду:",
    lblPaidTransaction: "Оплачена транзакція:",
    lblTotalSettled: "Разом сплачено:",
    lblReceptionTip: "Пред'явіть цей ID підтвердження або скріншот оплаченої транзакції на стійці рецепції при прибутті! Рецепція працює 24/7.",
    lblReserveRoom: "Забронювати",
    lblSecureCheckout: "Безпечна оплата",
    lblReceiptVoucher: "Ваучер квитанції",
    lblGuestRepresentative: "Представник гостя:",
    lblReservedRoom: "Зарезервований номер:",
    lblCheckInIntake: "Час заїзду:",
    lblCheckOutDeparture: "Час виїзду:",
    lblAuthorizedGuests: "Авторизовані гості:",
    lblGatewayTransactionRef: "Референс транзакції шлюзу:",
    lblReceptionTipModal: "Пред'явіть ваш ваучер підтвердження або прізвище бронювання в лобі хостелу при заїзді. Рецепція працює цілодобово 24/7.",
    lblShowLess: "згорнути",
    lblMore: "більше",
    lblCapacity: "Місткість",
    lblBedsLeft: "ліжок залишилось",
    lblBedLeft: "ліжко залишилось",
    bookingConfirmedTitle: "Бронювання підтверджено",
    bookingConfirmedDesc: "Вітаємо! Оплата успішно верифікована через LiqPay. Ліжко у Вашому номері надійно зарезервовано.",
    btnReturnExplore: "Повернутися на Головну",
    aboutText: "Cozzydine — українська компанія, заснована у 2020 році, яка спеціалізується на важливому секторі розміщення в індустрії гостинності та прагне надавати якісне тимчасове житло, яке ідеально обслуговує як внутрішніх, так і міжнародних мандрівників.\n\nХостел Cozzydine — це наше нове підприємство, спрямоване на надання доступного житла для широкого кола гостей — таких як бекпекери, автостопники, пари, цифрові кочівники, внутрішні мігранти, туристичні групи та бізнес-мандрівники.\n\nРозташований у центрі Києва, наш хостел ретельно розроблений, щоб запропонувати безпечне, зручно розташоване житло, обладнане всіма необхідними зручностями для комфортного проживання, відновлювального відпочинку та продуманого зв'язку з надихаючими мистецькими та культурними хабами незламності Києва.",
    sightseeingTag: "Інтерактивний туристичний путівник",
    sightseeingTitle: "Мистецькі, культурні хаби та центри незламності Києва",
    sightseeingDesc: "Хостел Cozzydine розташований за декілька хвилин ходьби від станцій метро, звідки можна дістатися центру Києва. Досліджуйте місцеві пам'ятки:",
    sight1Name: "Хрещатик та Майдан",
    sight1Desc: "Історична центральна площа міста з відкритими кафе, монументальними спорудами та вуличними артистами.",
    sight1Meters: "9 хв на метро",
    sight2Name: "Києво-Печерська лавра",
    sight2Desc: "Дивовижні золоті бані вікових храмів, густі монастирські сади та священні печери.",
    sight2Meters: "10 хв їзди",
    sight3Name: "Софійський собор та Золоті ворота",
    sight3Desc: "Величний собор XI століття зі списку спадщини ЮНЕСКО поруч із руїнами стародавньої середньовічної брами Києва.",
    sight3Meters: "12 хв на метро",
    sight4Name: "Андріївський узвіз та Поділ",
    sight4Desc: "Богемна мистецька вулиця Києва з художніми галереями, ярмарками народних майстрів та затишними кав'ярнями.",
    sight4Meters: "15 хв на метро",
    sight5Name: "Костел Святого Миколая",
    sight5Desc: "Казковий неоготичний архітектурний шедевр авторства Владислава Городецького, діючий Будинок органної музики.",
    sight5Meters: "4 хв їзди",
    sight6Name: "Монумент «Батьківщина-Мати»",
    sight6Desc: "Найвища монументальна сталева статуя України над Дніпром, що символізує стійкість та національну гордість.",
    sight6Meters: "10 хв їзди",
    sight7Name: "Володимирська гірка та парк",
    sight7Desc: "Мальовничий парк на схилах Дніпра зі старовинним пам'ятником і виходом на сучасний скляний пішохідний міст.",
    sight7Meters: "12 хв на метро",
    checklistTitle: "Транзитний чек-лист бекпекера",
    checklistSpot2: "Станція метро Либідська",
    checklistSpotBus: "Автостанція Либідська",
    checklistSpotCentralBus: "Центральний автовокзал Київ",
    checklistSpotRailway: "Центральний залізничний вокзал Київ",
    checklistSpot3: "Центр Києва",
    checklistWalkTip: "Заходьте у двір будинку 75а, на дзвінках чітко написано Cozzydine!",
    physicalEndpointLabel: "ФІЗИЧНА АДРЕСА ТА КОНТАКТИ",
    directChatsLabel: "ПРЯМІ ЧАТИ ТА СОЦМЕРЕЖІ",
    workingHoursLabel: "Робочі години:",
    workingHoursValue: "24/7 (Завжди відкрито)",

    // Room Details
    room_type_private: "Приватна кімната",
    room_type_dorm: "Загальна спальня",
    lblLongStayOfferTitle: "Спеціальна пропозиція",
    lblLongStayOfferDesc: "Забронюйте 3+ ночі зі знижкою 10% або 7+ ночей зі знижкою 15%.",
    "room_double-room_name": "Двомісний номер",
    "room_double-room_desc": "Комфортний приватний номер з одним великим ліжком, ідеальний для двох гостей. Включає безкоштовний Wi-Fi, свіжу білизну та стандартні зручності для чудового відпочинку.",
    "room_eight-bed-dorm_name": "8-місний загальний номер",
    "room_eight-bed-dorm_desc": "Бюджетний спільний номер з вісьмома комфортними двоярусними ліжками. Включає безкоштовний Wi-Fi, індивідуальні шафки під ключ, розетки, доступ до спільної ванної кімнати та міні-кухні.",
    "room_six-bed-dorm_name": "6-місний загальний номер",
    "room_six-bed-dorm_desc": "Затишний спільний номер із шістьма зручними двоярусними ліжками. Включає безкоштовний Wi-Fi, персональні шафки під ключ, розетки та повноцінний доступ до всіх зручностей.",
    "room_four-bed-womens-dorm_name": "4-місний жіночий номер",
    "room_four-bed-womens-dorm_desc": "Затишний суто жіночий спільний номер з чотирма зручними двоярусними ліжками. Включає швидкий Wi-Fi, шафки під ключ, розетки та доступ до ванної кімнати й кухні.",
    "room_four-bed-mens-dorm_name": "4-місний чоловічий номер",
    "room_four-bed-mens-dorm_desc": "Затишний суто чоловічий спільний номер з чотирма зручними двоярусними ліжками. Включає Wi-Fi, індивідуальні шафки із замком, розетки й повний доступ до кухні.",

    // Interactive Transit & Local guide tabs
    tabLybidska: "Метро Либідська, Вихід 2 (< 2 хв)",
    tabBus: "Автобусна зупинка (< 2 хв)",
    tabPalats: "Київський центр (Майдан Незалежності)",
    tabTrain: "Центральний вокзал (10 хв)",
    tabCentralBus: "Центральний автовокзал (3 хв)",

    // Metro Access & Transit Box
    metroAccessTitle: "🚇 Доступ до метро та транзиту",
    metroAccessDesc: "До хостелу Cozzydine у Києві неймовірно легко дістатися громадським транспортом. Ми знаходимося всього за кілька кроків від основних транспортних ліній:",
    metroAccessItem1: "Станція метро Либідська (Вихід 2) — менше 2 хв пішки",
    metroAccessItem2: "Київський центр (станція метро Майдан Незалежності) — 10 хв поїздки (5.3 км)",
    metroAccessItem3: "Київ-Пасажирський — 10 хв поїздки на метро",

    // Rooms Subtitle
    roomsSubtitle: "Затишні приватні кімнати та комфортні спільні номери, створені для кожного мандрівника. Знайдіть свій ідеальний простір, насолоджуйтеся преміум-зручностями та спілкуйтеся з легкістю.",

    // Testimonials
    testimonialsTag: "💬 Відгуки гостей",
    testimonialsTitle: "Відгуки гостей",
    testimonialsDesc: "Справжні перевірені відгуки та історії від наших бекпекерів, туристичних груп, бізнес-мандрівників та цифрових кочівників.",
    reviewsTitle: "Що говорять наші гості",

    // Guest Review Translations
    "rev_REV-01_content": "Чудове розташування, прямо біля метро та торгового центру Ocean Plaza. Охайно та чисто, а власник завжди на зв'язку 24/7, якщо у вас виникнуть питання або проблеми. Якщо ви шукаєте недороге житло в центрі Києва, я б щиро рекомендував хостел Cozzydine. Я обов'язково залишуся тут ще раз під час моєї наступної поїздки в Україну.",
    "rev_REV-01_tag1": "Поруч метро",
    "rev_REV-01_tag2": "Ocean Plaza",
    "rev_REV-01_tag3": "Доступний центр",
    "rev_REV-01_tag4": "Підтримка 24/7",
    "rev_REV-02_content": "Хостел дуже хороший на свою ціну, все чисто, ліжка неймовірно комфортні. Власник дуже привітний і завжди готовий допомогти. Рекомендую!",
    "rev_REV-02_tag1": "Чудова ціна",
    "rev_REV-02_tag2": "Супер чисто",
    "rev_REV-02_tag3": "Зручні ліжка",
    "rev_REV-02_tag4": "Привітний хост",
    "rev_REV-03_content": "Власник дуже сумлінно ставиться до підтримки порядку в хостелі та миттєво відповідає на будь-які прохання чи запити.",
    "rev_REV-03_tag1": "Дбайливий хост",
    "rev_REV-03_tag2": "Завжди чисто",
    "rev_REV-03_tag3": "Миттєва відповідь",
    "rev_REV-04_content": "Сподобалась комунікація зі мною як з не місцевою, постійно на звʼязку та пояснювали як добратися. В хостелі чисто та охайно, комфортне ліжко із чистою постіллю, є душ та кухня. Це підвальне приміщення а отже повітряна тривогу можна тут і перечекати.",
    "rev_REV-04_tag1": "Чудова комунікація",
    "rev_REV-04_tag2": "Чисто і охайно",
    "rev_REV-04_tag3": "Безпечне укриття",
    "rev_REV-04_tag4": "Комфортне ліжко",
    "rev_REV-05_content": "Я тут уже вдруге, і зупинитися тут знову було величезним задоволенням. Чінджу — чудовий і неймовірно професійний господар. У мене виникли проблеми з рейсом, я прибув дуже пізно, але пізній заїзд не став проблемою. Весь процес був надзвичайно простим і гнучким. Кімната знову виявилася бездоганно чистою і чудово обладнаною. Вид з балкона просто неперевершений. Розташування супер, особливо цікаво для шанувальників культури та мистецтва.",
    "rev_REV-05_tag1": "Постійний гість",
    "rev_REV-05_tag2": "Професійний хост",
    "rev_REV-05_tag3": "Чудовий балкон",
    "rev_REV-05_tag4": "Локація культури",
    "rev_REV-06_content": "Неймовірно сподобалося це місце! Передусім через ідеальне розташування, чудовий краєвид і найкращу ціну для цього міста! Це просте та елегантне місце. Забронював на дві ночі, а залишився на два тижні. Дуже рекомендую!",
    "rev_REV-06_tag1": "Подовжене проживання",
    "rev_REV-06_tag2": "Красивий вид",
    "rev_REV-06_tag3": "Елегантність та простота",
    "rev_REV-06_tag4": "Супер ціна",
    "rev_REV-07_content": "Готель Cozzydine став ідеальним притулком для нас, щоб заспокоїтися та відпочити після виснажливого перельоту. У нас був чудовий вид на місто, сам будинок надзвичайно чарівний, квартира бездоганна та простора. Наш господар Чінджу був найкращим.",
    "rev_REV-07_tag1": "Ідеальний спокій",
    "rev_REV-07_tag2": "Чисті апартаменти",
    "rev_REV-07_tag3": "Велика ванна кімната",
    "rev_REV-07_tag4": "Чудові види",
    "rev_REV-08_content": "Перебування в прекрасному готелі Cozzydine було наче у казці. Дякую Чінджу за підтримку та доступність 24/7, а також за те, що він такий чудовий господар. Обов'язково зупинюся тут знову в майбутньому!",
    "rev_REV-08_tag1": "Казкова атмосфера",
    "rev_REV-08_tag2": "Доступність 24/7",
    "rev_REV-08_tag3": "Дивовижний хост",
    "rev_REV-08_tag4": "Гарний декор",
    perNight: "/ ніч",
    bedsAvailable: "ліжок в наявності",
    bedAvailable: "ліжко в наявності",
    longStayDiscount: "Знижка за тривале перебування застосована!",
    lblPrivacyPolicy: "Політика конфіденційності",
    lblAllRightsReserved: "Всі права захищені.",
    lblPreparingBookingEngine: "Підготовка системи бронювання...",
    lblLoadingMap: "Завантаження карти розташування Cozzy...",
    lblEmail: "Електронна пошта:",
    lblCallFrontDesk: "Дзвінок (Рецепція):",
    lblHostel: "Хостел",
    lblTransit250m: "250 метрів",
    lblTransit300m: "300 метрів",
    lblTransit3m: "3 хв їзди",
    lblTransit10m: "10 хв їзди",
    lblCozzyAiAssistant: "Cozy AI",
    lblCozzyCoreAi: "Ядро Cozy AI"
  },
  de: {
    navHome: "Startseite",
    navRooms: "Zimmer & Schlafsäle",
    navAbout: "Über Uns",
    navContact: "Kontakt",
    btnBook: "Buchen Now",
    heroRating: "★ 4.9 Premium Boutique-Erlebnis",
    heroTitlePart1: "Gemütliches Hostel",
    heroTitlePart2: "in der Kiewer Innenstadt",
    heroSubtitle: "Willkommen im Hostel Cozzydine, einer ruhigen, sicheren und gut gelegenen Unterkunft in der Stadt. Mit gemütlichen Privatzimmern und Schlafsälen, die sich perfekt für Solo-Reisende, Paare, digitale Nomaden, Gruppen und Geschäftsreisende eignen.",
    heroBtnBook: "Jetzt buchen & sparen",
    heroBtnMap: "Gebietsführer ansehen",
    heroBtnContact: "KONTAKTIEREN SIE UNS",
    amenitiesLabel: "PREMIUM-WERTE",
    amenitiesTitle: "Ausstattung & Annehmlichkeiten",
    amenitiesDesc: "Durchdachte Annehmlichkeiten mit unverzichtbarem Reisekomfort, sicheren Wohnräumen und Highspeed-Internetbereichen.",
    amWifi: "Kostenloses Highspeed-WLAN",
    amSecurity: "Videoüberwachung (CCTV) & Sicherheit",
    amAc: "Klimaanlage/Belüftung",
    amKitchen: "Voll ausgestattete Küche",
    amWater: "Konstante Warmwasserversorgung",
    amLuggage: "Gepäckaufbewahrung",
    amLockers: "Sichere Schließfächer",
    amLaundry: "Selbstbedienungs-Waschsalon",
    amReception: "24-Stunden-Rezeption",
    promoTag: "UNTERSTÜTZUNG & SICHERHEIT",
    promoTitle: "Entdecken Sie Kiews strahlendes Erbe",
    promoDesc: "Kiew ist eine dynamische und widerstandsfähige Kulturhauptstadt. Entspannen Sie sich im Cozzydine mit sicherem Luftschutzraum, reaktionsschnellem Support und bester Verkehrsanbindung.",
    promoBtn: "Bett sichern & reservieren",
    aboutTitlePart1: "Maßgeschneiderte Gastfreundschaft",
    aboutTitlePart2: "Seit 2020",
    aboutQuarterTitle: "KIEW COZZYDINE HQ",
    aboutQuarterDesc: "Direkt verwaltet von Chinju Ravi und dem Kiewer Team mit mehrsprachiger Unterstützung.",
    contactTag: "IMMERSIVE PRÄSENZ",
    contactTitle: "Cozzydine kontaktieren",
    contactSubtitle: "Kontaktieren Sie uns für maßgeschneiderte Buchungsanforderungen, Ankunftshilfe oder Wegbeschreibungen.",
    contactFormTitle: "Eine Nachricht schreiben",
    contactSubmittedTitle: "Nachricht sicher übermittelt",
    contactSubmittedDesc: "Vielen Dank! Das Cozzydine-Team oder unser Cozy KI-Host antwortet Ihnen innerhalb von 15 Minuten.",
    contactLblName: "Ihr vollständiger Name",
    contactLblEmail: "E-Mail-Adresse",
    contactLblMsg: "Ihre Nachricht oder Anfrage",
    contactBtnSubmit: "Nachricht senden",

    roomType: "Zimmertyp",
    // Additional standard baseline keys
    home: "Startseite",
    rooms: "Zimmer & Schlafsäle",
    about: "Über Uns",
    contact: "Kontakt",
    guestName: "Gastname",
    guestEmail: "E-Mail-Adresse",
    checkIn: "Anreise",
    checkOut: "Abreise",
    guests: "Gäste",
    submit: "Senden",
    successMessage: "Erfolgreich übermittelt!",
    close: "Schließen",
    open: "Öffnen",
    learnMore: "Mehr erfahren",
    backToStart: "Zurück zum Anfang",
    policiesTitle: "Hostel-Richtlinien & Hausregeln",
    policyCatAll: "Alle Richtlinien",
    policyCatStay: "Anreise & Aufenthalt",
    policyCatFacilities: "Ausstattung & Zimmer",
    policyCatRules: "Hausordnung",
    policiesSubtitle: "Um einen friedlichen, sicheren und komfortablen Aufenthalt für alle Gäste zu gewährleisten, lesen Sie bitte unsere Richtlinien.",
    policyCheckInOutTitle: "Check-In / Out",
    policyCheckInOutDesc: "Check-in: 14:00-23:00. Check-out: bis 11:00. Ein früher Check-in oder später Check-out ist je nach Verfügbarkeit, vorheriger Anmeldung und Genehmigung möglich.",
    policyQuietTitle: "Ruhezeiten",
    policyQuietDesc: "In allen Zimmern und Gemeinschaftsbereichen gilt von 23:00 bis 08:00 Uhr die Ruhezeit.",
    policyPaymentTitle: "Ausweis-, Zahlungs- & Altersbestimmungen",
    policyPaymentDesc: "Wir bevorzugen Kartenzahlung, und für den Check-in ist ein gültiger behördlicher Lichtbildausweis oder Reisepass erforderlich. Bitte beachten Sie, dass für alle Schlafsäle ein Mindestalter von 18 Jahren gilt.",
    policyCancellationTitle: "Stornierungsbedingungen",
    policyCancellationDesc: "• Standardtarife: Stornierungen oder Änderungen sind bei einer vorherigen Ankündigung bis zu 48 Stunden vor der Anreise kostenlos; bei verspäteten Änderungen wird eine Stornogebühr in Höhe von einer Nacht erhoben.\n\n• Nicht erstattungsfähige Tarife: Der gesamte Buchungsbetrag wird bei der Reservierung berechnet und ist strengstens nicht erstattungsfähig und nicht änderbar.\n\n• Nichtanreise (No-Shows): Ein Nichterscheinen ohne vorherige Benachrichtigung führt zur sofortigen Stornierung und zur Berechnung des vollen Aufenthalts.",
    policyLuggageTitle: "Gepäckaufbewahrung",
    policyLuggageDesc: "Wir bieten eine kostenlose Gepäckaufbewahrung zur Abholung am selben Tag an, die sowohl vor dem Check-in als auch nach dem Check-out verfügbar ist. Bitte fragen Sie diesen Service im Voraus an oder informieren Sie die Rezeption, bevor Sie Ihr Gepäck abstellen.",
    policyLaundryTitle: "Wäscheservice",
    policyLaundryDesc: "Selbstbedienungswaschsalon vor Ort verfügbar. Die Kosten pro Waschgang betragen 100 UAH; bitte an der Rezeption nach der Verfügbarkeit fragen. Bitte nasse Wäsche oder Handtücher nicht in den Schlafsälen aufhängen, sondern den dafür vorgesehenen Trockenraum benutzen.",
    policyComfortTitle: "Alkohol, Drogen & Rauchen",
    policyComfortDesc: "Der Besitz, der Vertrieb oder der Konsum von Alkohol und illegalen Substanzen ist strengstens verboten; Rauchen und Dampfen sind im Hostel gänzlich untersagt.",
    policyShelterTitle: "Schutzraum & Sicherheit",
    policyShelterDesc: "Das Hostel befindet sich im Erdgeschoss und im Untergeschoss, die U-Bahn-Station ist ebenfalls ganz in der Nähe.",
    policyVisitorsTitle: "Keine Besucher",
    policyVisitorsDesc: "Externe Gäste ohne Buchung haben keinen Zutritt.",
    policyFoodTitle: "Schlafsaal-Etikette",
    policyFoodDesc: "Das Essen oder Lagern offener Lebensmittel und Getränke in den Schlafsälen ist verboten, um die Sauberkeit zu wahren und Ungeziefer vorzubeugen. Bitte bewahren Sie Ihr Gepäck und Ihre persönlichen Gegenstände in Ihrem dafür vorgesehenen Schließfach oder unter Ihrem Bett auf. Verteilen Sie Ihre Sachen nicht auf dem Boden, in Durchgängen oder auf nicht zugewiesenen Betten.",
    policyKitchenTitle: "Toilette & Kitchenette",
    policyKitchenDesc: "Waschen und verstauen Sie alle Küchenutensilien sofort nach Gebrauch. Spülen Sie die Toilette immer ab, wischen Sie Oberflächen ab und wischen Sie den Boden vor dem Verlassen.",
    policyLockersTitle: "Schließfächer",
    policyLockersDesc: "Freistehende Schließfächer stehen in den Schlafsälen des Hostels zur Sicherung persönlicher Gegenstände zur Verfügung.",
    policyWorkspaceTitle: "Kostenloses Highspeed-WLAN",
    policyWorkspaceDesc: "Ja, kostenloses Highspeed-WLAN ist im gesamten Hostel verfügbar.",
    exploreRooms: "Zimmer erkunden",
    galleryLabel: "Vibe Tour",
    galleryTitle: "Bildergalerie",
    gallerySubtitle: "Erleben Sie eine visuelle Reise durch die gemütlichen Ecken des Hostels, erstklassigen Gemeinschaftsräume und ruhigen Zimmer.",
    stampGalleryBadge: "Kiewer Briefmarken-Edition",
    lightboxPostage: "Kiewer Archivsammlung",
    slide1Title: "Gemütliche Lounge",
    slide1Desc: "Der perfekte Ort zum Arbeiten, Lesen, Spielen oder um Kontakte zu knüpfen.",
    slide2Title: "Komfortable Hostelbetten",
    slide2Desc: "Orthopädische Matratzen, Steckdosen, Leselampen und dicke Vorhänge für erholsamen Schlaf.",
    slide3Title: "Gemütliches florales Doppelzimmer",
    slide3Desc: "Ein helles und ordentliches Doppelzimmer mit typischer lila-weißer Bettwäsche mit Blumenmuster, elegantem Wandspiegel, Kleiderbügel und einer Kommode.",
    slide4Title: "Gemeinschafts-Küchenzeile",
    slide4Desc: "Ausgestattet mit Mikrowelle, Wasserkocher, Toaster und Geschirr. Perfekt für einen Snack und zum Plaudern.",
    slide5Title: "Premium Zweibettzimmer",
    slide5Desc: "Ein ruhiger privater Rückzugsort mit luxuriöser Bettwäsche, Arbeitsbereich und modernem Dekor.",
    slide6Title: "Gemütlicher Innenhof-Eingang",
    slide6Desc: "Unser ruhiger Innenhof in klassischem Schwarz-Weiß-Design, nur wenige Schritte von Lybidska entfernt.",
    filterAll: "Alle",
    filterPrivate: "Privatzimmer",
    filterDorms: "Schlafsäle",
    directionsTitle: "Anfahrtsbeschreibung & Nahverkehr",
    directionsSubtitle: "Wie Sie das Hostel Cozzydine von U-Bahn und Bahnhöfen aus erreichen.",
    transitLybidskaLabel: "U-Bahn Lybidska Ausg. 2 (< 2 Min.)",
    transitBus: "Bushaltestelle Lybidska (< 2 Min.)",
    transitPalatsLabel: "Kiewer Stadtzentrum (10 Min.)",
    transitTrainLabel: "Hauptbahnhof (10 Min.)",
    monumentLabel: "🏛️ Unabhängigkeitsdenkmal, Kiew",
    roomsAndDorms: "Zimmer & Schlafsäle",
    aerialLabel: "🌉 Luftbild der Kiewer Altstadt",
    step1of3: "Schritt 1 von 3",
    step2of3: "Schritt 2 von 3",
    step3of3: "Schritt 3 von 3",
    lblGuestFullName: "Vollständiger Name",
    lblGuestEmailAddress: "E-Mail-Adresse",
    lblGuestNumber: "Anzahl der Gäste",
    lblGuestSingular: "Gast",
    lblGuestPlural: "Gäste",
    lblDailyRate: "Tagespreis:",
    lblSelectedRange: "Zeitraum:",
    lblEstimatedTotal: "Gesamtsumme:",
    lblNights: "Nächte",
    lblSelected: "Ausgewählt",
    lblInRange: "Im Zeitraum",
    lblSelectionPreview: "Vorschau",
    lblBooked: "Gebucht",
    widgetTitle: "Website-Sprache",
    translatorTitle: "Website-Übersetzung",
    changeAppLang: "App-Sprache ändern",
    placeholderTranslate: "Geben Sie eine Phrase zum Übersetzen ein...",
    translationResult: "Übersetzungsergebnis",
    copy: "Kopieren",
    copied: "Kopiert",
    translatingProgress: "Generiere perfekte Übersetzung...",
    btnTranslate: "Übersetzen",
    btnTranslating: "Übersetze...",
    phrasebook: "Hostel-Phrasebuch",
    from: "Von",
    to: "Nach",
    lblChooseCheckIn: "Bitte wählen Sie das Anreisedatum auf dem Kalender aus.",
    lblChooseCheckOut: "Hervorragend! Wählen Sie nun das Abreisedatum.",
    lblPaymentMethod: "Zahlungsart wählen",
    payOnlineLiqPay: "Sichere Online-Zahlung (LiqPay)",
    payOnlineDetails: "Sofortige Bestätigung • Karte / Privat24",
    payOnlineDetailsForm: "Sofortige Bestätigung • Visa / Mastercard / Privat24",
    payAtReception: "Zahlung vor Ort",
    payAtReceptionDetails: "Barzahlung oder Karte bei der Ankunft",
    payAtReceptionDetailsForm: "Keine Kreditkarte erforderlich • Bezahlen Sie vor Ort",
    btnConfirmReservation: "Reservierung bestätigen",
    btnProceedToCheckout: "Weiter zur Kasse",
    lblContactPhone: "Kontakt-Telefonnummer",
    lblPhonePlaceholder: "z.B. 50 123 4567",
    lblSecureHotelReservation: "Sichere Hotelreservierung",
    lblCheckoutReady: "Bereit zum Bezahlen",
    lblReservationConfirmedHeader: "RESERVIERUNG BESTÄTIGT",
    lblCongratulations: "Herzlichen Glückwunsch",
    lblReservedSafely: "ist sicher reserviert.",
    lblConfirmationId: "Bestätigungs-ID:",
    lblCheckInDate: "Check-In-Datum:",
    lblCheckOutDate: "Check-Out-Datum:",
    lblPaidTransaction: "Bezahlter Betrag:",
    lblTotalSettled: "Gesamtbetrag:",
    lblReceptionTip: "Präsentieren Sie diese Bestätigungs-ID oder den Zahlungsbeleg bei Ihrer Ankunft an der Rezeption! Die Lobby ist 24/7 geöffnet.",
    lblReserveRoom: "Reservieren",
    lblSecureCheckout: "Sicheres Checkout",
    lblReceiptVoucher: "Quittungsbeleg",
    lblGuestRepresentative: "Gast-Repräsentant:",
    lblReservedRoom: "Reserviertes Zimmer:",
    lblCheckInIntake: "Check-In-Einlass:",
    lblCheckOutDeparture: "Check-Out-Abreise:",
    lblAuthorizedGuests: "Autorisierte Gäste:",
    lblGatewayTransactionRef: "Zahlungsreferenz:",
    lblReceptionTipModal: "Präsentieren Sie Ihre Reservierungs-ID oder Ihren Namen bei der Ankunft in der Lobby des Hostels. Die Rezeption ist 24/7 besetzt.",
    lblShowLess: "weniger anzeigen",
    lblMore: "mehr",
    lblCapacity: "Kapazität",
    lblBedsLeft: "Betten übrig",
    lblBedLeft: "Bett übrig",
    bookingConfirmedTitle: "Buchung Bestätigt & Genehmigt",
    bookingConfirmedDesc: "Herzlichen Glückwunsch! Ihre Buchung ist gesichert und bestätigt. Ihre Unterkunft in Kiew ist reserviert.",
    btnReturnExplore: "Zurück zur Übersicht",
    aboutText: "Cozzydine ist ein im Jahr 2020 gegründetes ukrainisches Unternehmen, das sich auf den wichtigen Beherbergungssektor der Hotellerie spezialisiert hat und sich der Bereitstellung hochwertiger temporärer Unterkünfte widmet, die sowohl in- als auch ausländischen Reisenden nahtlos dienen.\n\nHostel Cozzydine ist unser neues Projekt, das darauf abzielt, preiswerte Unterkünfte für eine breite Palette von Gästen wie Backpacker, Tramper, Paare, Remote-Kreative, urbane Migranten, Gruppenentdecker und Geschäftsreisende gleichermaßen anzubieten.\n\In der Kiewer Innenstadt gelegen, ist unser Haus sorgfältig darauf ausgelegt, eine sichere und gut angebundene Unterkunft zu bieten, die mit allen wesentlichen Annehmlichkeiten für einen angenehmen Aufenthalt, erholsamen Schlaf und eine kuratierte Anbindung an Kiews inspirierende Kunst- und Kultur-Resilienz-Zentren ausgestattet ist.",
    sightseeingTag: "Interaktiver Reiseführer",
    sightseeingTitle: "Kiews Kunst-, Kultur- & Resilienz-Zentren",
    sightseeingDesc: "Das Hostel Cozzydine profitiert von der direkten Nähe zu U-Bahn-Verbindungen, die Sie direkt in die Innenstadt von Kiew bringen. Erkunden Sie lokale Sehenswürdigkeiten:",
    sight1Name: "Chreschtschatyk & Maidan",
    sight1Desc: "Der historische Stadtplatz mit Straßencafés, monumentalen Plätzen und lebhaften Straßenkünstlern.",
    sight1Meters: "9 Min. mit der U-Bahn",
    sight2Name: "Kiewer Höhlenkloster",
    sight2Desc: "Prachtvolle, jahrhundertealte goldene Kuppeln, üppige Wälder und heilige unterirdische Höhlengänge.",
    sight2Meters: "10 Min. Fahrt",
    sight3Name: "Sophienkathedrale & Goldenes Tor",
    sight3Desc: "Eine atemberaubende UNESCO-Welterbe-Kathedrale aus dem 11. Jahrhundert gepaart mit den alten Ruinen des Kiewer Tores.",
    sight3Meters: "12 Min. mit der U-Bahn",
    sight4Name: "Andreassteig & Podil",
    sight4Desc: "Kiews böhmische Kunststraße voller Galerien, Kunsthandwerksmärkte und gemütlicher Kaffeespezialitäten-Cafés.",
    sight4Meters: "15 Min. mit der U-Bahn",
    sight5Name: "St.-Nikolaus-Kathedrale",
    sight5Desc: "Ein beeindruckendes neugotisches Meisterwerk von Wladyslaw Horodezki, das auch als Haus der Orgelmusik dient.",
    sight5Meters: "4 Min. Fahrt",
    sight6Name: "Mutter-Heimat-Statue (Rodyna-Mat)",
    sight6Desc: "Ukrainische gigantische Monumentalstatue aus Stahl über dem Dnipro, die für Resilienz und Nationalstolz steht.",
    sight6Meters: "10 Min. Fahrt",
    sight7Name: "Wolodymyr-Hügel & Park",
    sight7Desc: "Ein ruhiger Park auf einem Hügel mit Blick auf den Dnipro, dem historischen Denkmal und der malerischen Fußgängerbrücke.",
    sight7Meters: "12 Min. Fahrt",
    checklistTitle: "Backpacker-Transit-Checkliste",
    checklistSpot2: "U-Bahn-Station Lybidska",
    checklistSpotBus: "Busbahnhof Lybidska",
    checklistSpotCentralBus: "Zentraler Busbahnhof Kiew",
    checklistSpotRailway: "Zentralbahnhof Kiew",
    checklistSpot3: "Kiewer Stadtzentrum",
    checklistWalkTip: "Gehen Sie direkt in den Innenhof 75a, die Türklingeln sind deutlich mit Cozzydine beschriftet!",
    physicalEndpointLabel: "PHYSISCHE KONTAKTDATEN",
    directChatsLabel: "DIREKTE CHATS & SOZIALE NETZWERKE",
    workingHoursLabel: "Öffnungszeiten:",
    workingHoursValue: "24/7 (Immer geöffnet)",

    // Room Details
    room_type_private: "Privatzimmer",
    room_type_dorm: "Mehrbettzimmer",
    lblLongStayOfferTitle: "Langzeit-Angebot",
    lblLongStayOfferDesc: "Buchen Sie 3+ Nächte für 10% Rabatt, oder 7+ Nächte für 15% Rabatt.",
    "room_double-room_name": "Doppelzimmer",
    "room_double-room_desc": "Komfortables Privatzimmer mit einem großen Bett, perfekt für zwei Gäste. Inklusive kostenlosem WLAN, frischer Bettwäsche und Standard-Annehmlichkeiten für einen erholsamen Aufenthalt.",
    "room_eight-bed-dorm_name": "8-Bett-Schlafsaal",
    "room_eight-bed-dorm_desc": "Preisgünstiges Mehrbettzimmer mit acht bequemen Etagenbetten. Inklusive kostenlosem WLAN, persönlichen Schließfächern, Steckdosen, Zugang zu Gemeinschaftsbad & Küchenzeile.",
    "room_six-bed-dorm_name": "6-Bett-Schlafsaal",
    "room_six-bed-dorm_desc": "Gemütliches Mehrbettzimmer mit sechs komfortablen Etagenbetten. Inklusive freiem WLAN, persönlichen Schließfächern, Steckdosen und voll ausgestattetem Küchenzugang.",
    "room_four-bed-womens-dorm_name": "4-Bett-Frauenschlafsaal",
    "room_four-bed-womens-dorm_desc": "Gemütlicher, reiner Frauenschlafsaal mit vier komfortablen Etagenbetten. Inklusive schnellem WLAN, Schließfächern, Steckfächern und Zugang zu Bad und Küche.",
    "room_four-bed-mens-dorm_name": "4-Bett-Männerschlafsaal",
    "room_four-bed-mens-dorm_desc": "Gemütlicher, reiner Männerschlafsaal mit vier Etagenbetten. Inklusive erstklassigem WLAN-Zugang, Schließfächern, Steckdosen und Zugang zur Küchenzeile.",

    // Interactive Transit & Local guide tabs
    tabLybidska: "U-Bahn Lybidska Ausg. 2 (< 2 Min.)",
    tabBus: "Bushaltestelle Lybidska (< 2 Min.)",
    tabPalats: "Kiewer Stadtzentrum (Maidan Nezalezhnosti)",
    tabTrain: "Hauptbahnhof (10 Min.)",
    tabCentralBus: "Zentraler Busbahnhof (3 Min.)",

    // Metro Access & Transit Box
    metroAccessTitle: "🚇 U-Bahn-Anbindung & Transit",
    metroAccessDesc: "Das Hostel Cozzydine Kyiv ist mit öffentlichen Verkehrsmitteln unglaublich leicht zu erreichen. Wir sind nur einen kurzen Spaziergang von den wichtigsten Metrolinien entfernt:",
    metroAccessItem1: "U-Bahn-Station Lybidska (Ausgang 2) — weniger als 2 Minuten zu Fuß",
    metroAccessItem2: "Kiewer Stadtzentrum (U-Bahnhof Maidan Nezalezhnosti) — 10 Min. Fahrt (5,3 km)",
    metroAccessItem3: "Hauptbahnhof Kiew — 10 Minuten mit der U-Bahn",

    // Rooms Subtitle
    roomsSubtitle: "Komfortable Privatzimmer und gesellige Schlafsäle für jeden Reisenden. Finden Sie Ihre perfekte Unterkunft, genießen Sie hervorragende Annehmlichkeiten und reisen Sie entspannt.",

    // Testimonials
    testimonialsTag: "💬 Gästebewertungen",
    testimonialsTitle: "Gästeerfahrungen",
    testimonialsDesc: "Echtes, authentisches Feedback und Geschichten von unseren Backpackern, Gruppenreisenden, Geschäftsreisenden und digitalen Nomaden.",
    reviewsTitle: "Was unsere Gäste sagen",

    // Guest Review Translations
    "rev_REV-01_content": "Tolle Lage, direkt neben der U-Bahn und dem Einkaufszentrum Ocean Plaza. Sauber und ordentlich und der Besitzer ist rund um die Uhr vor Ort, falls Sie Fragen oder Probleme haben. Wenn Sie eine erschwingliche Unterkunft im Zentrum von Kiew suchen, kann ich das Cozzydine nur wärmstens empfehlen. Ich werde auf jeden Fall wieder hier übernachten.",
    "rev_REV-01_tag1": "U-Bahnnähe",
    "rev_REV-01_tag2": "Ocean Plaza",
    "rev_REV-01_tag3": "Zentrumsnah",
    "rev_REV-01_tag4": "24/7 Support",
    "rev_REV-02_content": "Gastgeberfreundlich und hilfsbereit. Unterkunft sauber und gemütlich, sehr bequeme Betten.",
    "rev_REV-02_tag1": "Super Preis",
    "rev_REV-02_tag2": "Sehr sauber",
    "rev_REV-02_tag3": "Bequeme Betten",
    "rev_REV-02_tag4": "Freundlicher Host",
    "rev_REV-03_content": "Der Gastgeber bemüht sich sehr darum, den Ort ordentlich zu halten, und reagiert extrem schnell auf alle Anfragen.",
    "rev_REV-03_tag1": "Aufmerksamer Host",
    "rev_REV-03_tag2": "Immer ordentlich",
    "rev_REV-03_tag3": "Sehr reaktionsschnell",
    "rev_REV-04_content": "Mir gefiel die Kommunikation mit mir als Ortsfremde sehr, sie waren ständig in Kontakt und erklärten mir genau, wie ich dorthin komme. Das Hostel ist sauber und ordentlich, es gibt bequeme Betten mit frischer Wäsche, Dusche und Küche. Da es im Untergeschoss liegt, kann man hier auch Luftalarme sicher überstehen.",
    "rev_REV-04_tag1": "Tolle Kommunikation",
    "rev_REV-04_tag2": "Sauber & Ordentlich",
    "rev_REV-04_tag3": "Sicherer Schutzraum",
    "rev_REV-04_tag4": "Bequemes Bett",
    "rev_REV-05_content": "Ich war zum zweiten Mal hier und es war mir ein Vergnügen. Chinju ist ein fantastischer und professioneller Gastgeber. Wegen Flugproblemen kam ich sehr spät an, aber das war überhaupt kein Problem. Alles war extrem flexibel. Das Zimmer war wieder sehr sauber und gut ausgestattet. Der Ausblick vom Balkon ist herrlich. Sehr zentral gelegen, wenn man sich für Kunst interessiert.",
    "rev_REV-05_tag1": "Stammgast",
    "rev_REV-05_tag2": "Aufsicht vor Ort",
    "rev_REV-05_tag3": "Toller Balkonblick",
    "rev_REV-05_tag4": "Kultur-Zentrum",
    "rev_REV-06_content": "Ich habe diesen Ort geliebt! Vor allem wegen der exzellenten Lage, der wunderbaren Aussicht und dem absolut unschlagbaren Preis. Es ist schlicht und elegant. Ich hatte für zwei Nächte gebucht und blieb zwei Wochen! Ich kann es jedem nur ans Herz legen.",
    "rev_REV-06_tag1": "Verlängerter Aufenthalt",
    "rev_REV-06_tag2": "Herrliche Aussicht",
    "rev_REV-06_tag3": "Einfach & Elegant",
    "rev_REV-06_tag4": "Bester Preis",
    "rev_REV-07_content": "Das Hotel Cozzydine war der perfekte Ort für uns, um nach einem anstrengenden Flug zur Ruhe zu kommen und zu entspannen. Wir hatten eine wunderbare Sicht auf die Stadt, das Gebäude hat viel Charme, die Wohnung war makellos rein, geräumig und das Badezimmer ist sehr groß und komfortabel. Chinju war großartig.",
    "rev_REV-07_tag1": "Ruhe-Oase",
    "rev_REV-07_tag2": "Makellose Wohnung",
    "rev_REV-07_tag3": "Großes Badezimmer",
    "rev_REV-07_tag4": "Exzellente Aussicht",
    "rev_REV-08_content": "Es war wie im Märchen in diesem wunderschönen Hotel Cozzydine. Vielen Dank an Chinju, der rund um die Uhr erreichbar war und ein so toller Gastgeber ist. Ich werde in Zukunft auf jeden Fall wieder hier übernachten!",
    "rev_REV-08_tag1": "Märchenhafter Vibe",
    "rev_REV-08_tag2": "24/7 Erreichbarkeit",
    "rev_REV-08_tag3": "Toller Gastgeber",
    "rev_REV-08_tag4": "Wunderschönes Dekor",
    perNight: "/ Nacht",
    bedsAvailable: "Betten verfügbar",
    bedAvailable: "Bett verfügbar",
    longStayDiscount: "Langzeit-Rabatt angewendet!",
    lblPrivacyPolicy: "Datenschutzerklärung",
    lblAllRightsReserved: "Alle Rechte vorbehalten.",
    lblPreparingBookingEngine: "Buchungsmaschine wird vorbereitet...",
    lblLoadingMap: "Laden der Cozzy-Standortkarte...",
    lblEmail: "E-Mail:",
    lblCallFrontDesk: "Anrufen (Rezeption):",
    lblHostel: "Hostel",
    lblTransit250m: "250 Meter",
    lblTransit300m: "300 Meter",
    lblTransit3m: "3 Min. Fahrt",
    lblTransit10m: "10 Min. Fahrt",
    lblCozzyAiAssistant: "Cozy AI",
    lblCozzyCoreAi: "Cozy Core AI"
  },
  es: {
    navHome: "Inicio",
    navRooms: "Habitaciones y Dormitorios",
    navAbout: "Nosotros",
    navContact: "Contacto",
    btnBook: "Reservar",
    heroRating: "★ 4.9 Excelente Experiencia Boutique",
    heroTitlePart1: "Hostel acogedor",
    heroTitlePart2: "en el centro de Kyiv",
    heroSubtitle: "Bienvenido a Hostel Cozzydine, un alojamiento tranquilo, seguro y bien ubicado en la ciudad. Con cómodas habitaciones privadas y dormitorios compartidos, perfectos para viajeros solitarios, parejas, creadores remotos, exploradores en grupo y huéspedes de negocios por igual.",
    heroBtnBook: "Reservar y Guardar",
    heroBtnMap: "Guía de la Zona",
    heroBtnContact: "CONTÁCTANOS",
    amenitiesLabel: "VALORES PREMIUM",
    amenitiesTitle: "Instalaciones y Servicios",
    amenitiesDesc: "Servicios seleccionados con comodidades de viaje esenciales, espacios de vida seguros y zonas de internet de alta velocidad.",
    amWifi: "Wi-Fi gratuito de alta velocidad",
    amSecurity: "Vigilancia por CCTV y seguridad",
    amAc: "Aire acondicionado y ventilación",
    amKitchen: "Cocina Totalmente Equipada",
    amWater: "Suministro constante de agua caliente",
    amLuggage: "Almacenamiento de equipaje",
    amLockers: "Casilleros seguros",
    amLaundry: "Lavandería de autoservicio",
    amReception: "Recepción las 24 horas",
    promoTag: "INICIATIVA DE APOYO",
    promoTitle: "Explora la Herencia de Ucrania",
    promoDesc: "Kyiv es una capital dinámica de arte, cultura y resiliencia. Descanse tranquilo en Cozzydine con nuestro refugio seguro, soporte atento y excelente transporte.",
    promoBtn: "Asegurar Habitación y Reservar",
    aboutTitlePart1: "Hospitalidad a Medida",
    aboutTitlePart2: "Desde 2020",
    aboutQuarterTitle: "Cozzydine Kyiv HQ",
    aboutQuarterDesc: "Gestionado directamente por Chinju Ravi y el equipo de Kyiv con asistencia bilingüe.",
    contactTag: "SIEMPRE EN EL LUGAR",
    contactTitle: "Contactar a Cozzydine",
    contactSubtitle: "Contáctenos para requisitos de reserva personalizados, soporte de llegada o indicaciones de ubicación.",
    contactFormTitle: "Escríbenos Directamente",
    contactSubmittedTitle: "Mensaje Enviado con Éxito",
    contactSubmittedDesc: "¡Gracias! El personal de Cozzydine o el asistente Cozy AI responderán en un plazo de 15 minutos.",
    contactLblName: "Su Nombre Completo",
    contactLblEmail: "Dirección de Correo",
    contactLblMsg: "Su Mensaje o Pregunta",
    contactBtnSubmit: "Enviar Mensaje",

    roomType: "Tipo de Habitación",
    // Additional standard baseline keys
    home: "Inicio",
    rooms: "Habitaciones y Dormitorios",
    about: "Nosotros",
    contact: "Contacto",
    guestName: "Nombre del Huésped",
    guestEmail: "Correo Electrónico",
    checkIn: "Fecha de Entrada",
    checkOut: "Fecha de Salida",
    guests: "Huéspedes",
    submit: "Enviar",
    successMessage: "¡Enviado con éxito!",
    close: "Cerrar",
    open: "Abrir",
    learnMore: "Leer más",
    backToStart: "Volver al inicio",
    policiesTitle: "Políticas del Albergue y Normas de Casa",
    policyCatAll: "Todas las políticas",
    policyCatStay: "Llegada y Estancia",
    policyCatFacilities: "Instalaciones",
    policyCatRules: "Reglas de la casa",
    policiesSubtitle: "Para garantizar una estancia tranquila, segura y cómoda para todos los huéspedes, lea nuestras directrices principales.",
    policyCheckInOutTitle: "Check-In / Out",
    policyCheckInOutDesc: "Entrada: 14:00-23:00. Salida: antes de las 11:00. El registro de entrada temprano y el de salida tardío están sujetos a disponibilidad, notificación previa y aprobación.",
    policyQuietTitle: "Horas de Silencio",
    policyQuietDesc: "Las horas de silencio se aplican en todas las habitaciones y áreas comunes de 23:00 a 08:00.",
    policyPaymentTitle: "Política de Identificación, Pago y Edad",
    policyPaymentDesc: "Preferimos los pagos con tarjeta, y se requiere un documento de identidad oficial con fotografía vigente o pasaporte para registrarse. Tenga en cuenta que se aplica un requisito de edad mínima de 18 años para todos los dormitorios.",
    policyCancellationTitle: "Política de Cancelación",
    policyCancellationDesc: "• Tarifas Estándar: Las cancelaciones o modificaciones son gratuitas si se solicitan con previo aviso hasta 48 horas antes de la llegada; los cambios tardíos conllevan una penalización equivalente al coste de una noche.\n\n• Tarifas No Reembolsables: El importe total de la reserva se cobra al hacer la reserva y es estrictamente no reembolsable y no modificable.\n\n• No Presentado (No-Shows): La falta de asistencia sin previo aviso da lugar a la cancelación inmediata de la reserva y al cobro de la estancia completa.",
    policyLuggageTitle: "Equipaje",
    policyLuggageDesc: "Ofrecemos almacenamiento de equipaje gratuito para la recogida el mismo día, disponible tanto antes del registro de entrada como después del registro de salida. Solicite este servicio con antelación o informe a recepción antes de dejar sus maletas.",
    policyLaundryTitle: "Lavandería",
    policyLaundryDesc: "Instalaciones de autoservicio disponibles en el establecimiento. El coste por lavado es de 100 UAH, consulte en recepción y confirme disponibilidad. No cuelgue ropa húmeda ni toallas en los dormitorios, use la sala de secado designada.",
    policyComfortTitle: "Alcohol, Drogas y Tabaco",
    policyComfortDesc: "La posesión, distribución o consumo de alcohol y sustancias ilegales está estrictamente prohibida; fumar y vapear están totalmente prohibidos en el albergue.",
    policyShelterTitle: "Refugio Fuerte",
    policyShelterDesc: "El albergue está ubicado en la planta baja y en el sótano, y la estación de metro también está cerca.",
    policyVisitorsTitle: "Sin Visitas",
    policyVisitorsDesc: "Se prohíbe el acceso a personas sin reserva.",
    policyFoodTitle: "Etiqueta del Dormitorio",
    policyFoodDesc: "Se prohíbe comer o guardar alimentos y bebidas abiertos en los dormitorios compartidos para mantener la limpieza y evitar plagas. Guarde su equipaje y pertenencias personales dentro de su casillero asignado o debajo de su cama. No esparza sus pertenencias por el suelo, los pasillos o en camas no asignadas.",
    policyKitchenTitle: "Aseo y Cocina pequeña",
    policyKitchenDesc: "Lave y guarde todos los utensilios de cocina inmediatamente después de usarlos. Tire de la cadena del inodoro siempre, limpie las superficies y friegue el suelo antes de salir.",
    policyLockersTitle: "Casilleros",
    policyLockersDesc: "Hay casilleros independientes disponibles en los dormitorios del albergue para guardar objetos personales de forma segura.",
    policyWorkspaceTitle: "Wi-Fi gratis de alta velocidad",
    policyWorkspaceDesc: "Sí, hay Wi-Fi gratuito de alta velocidad disponible en todo el albergue.",
    exploreRooms: "Explorar Habitaciones",
    galleryLabel: "Tour visual",
    galleryTitle: "Galería del Hostal",
    gallerySubtitle: "Realice un recorrido visual por los acogedores rincones de Cozzydine, sus áreas sociales premium y sus tranquilos dormitorios.",
    galleryAlt_1: "Habitación doble, Hostel Cozzydine, Kyiv",
    galleryAlt_2: "Pasillo e interior, Hostel Cozzydine, Kyiv",
    galleryAlt_3: "Recepción y vestíbulo, Hostel Cozzydine, Kyiv",
    galleryAlt_4: "Habitación doble privada, Hostel Cozzydine, Kyiv",
    galleryAlt_5: "Habitación doble, Hostel Cozzydine, Kyiv",
    galleryAlt_6: "Dormitorio de 4 camas, Hostel Cozzydine, Kyiv",
    galleryAlt_7: "Dormitorio de 6 camas, Hostel Cozzydine, Kyiv",
    stampGalleryBadge: "Edición Filatélica de Kiev",
    lightboxPostage: "Colección Archivos de Kiev",
    slide1Title: "Salón Compartido Acogedor",
    slide1Desc: "El lugar perfecto para trabajar, leer, jugar o conectar con viajeros internacionales.",
    slide2Title: "Camas Cómodas de Dormitorio",
    slide2Desc: "Colchones ortopédicos, enchufes directos, luces de lectura y cortinas opacas para un descanso profundo.",
    slide3Title: "Habitación Doble Floral Acogedora",
    slide3Desc: "Una habitación doble luminosa y ordenada con ropa de cama floral morada y blanca característica, un elegante espejo de pared, perchero y una cómoda de cajones.",
    slide4Title: "Kitchenette Social",
    slide4Desc: "Equipada con microondas, hervidor, tostadora y utensilios. Prepare algo ligero y haga nuevos amigos.",
    slide5Title: "Habitación Twin Premium",
    slide5Desc: "Un tranquilo santuario privado con ropa de cama de lujo, mesa de trabajo y decoración de primera.",
    slide6Title: "Encantadora Entrada al Patio",
    slide6Desc: "Nuestro tranquilo patio interior pintado en el característico blanco y negro, a pasos de Lybidska.",
    filterAll: "Todas",
    filterPrivate: "Privadas",
    filterDorms: "Dormitorios",
    directionsTitle: "Guía de Direcciones y Transporte",
    directionsSubtitle: "Cómo llegar fácilmente al Hostal Cozzydine desde el metro y terminales.",
    transitLybidskaLabel: "Metro Lybidska Salida 2 (< 2 min)",
    transitBus: "Parada de autobús Lybidska (< 2 min)",
    transitPalatsLabel: "Centro de Kyiv (Maidan Nezalezhnosti)",
    transitTrainLabel: "Estación Central (10 min)",
    monumentLabel: "🏛️ Monumento a la Independencia, Kyiv",
    roomsAndDorms: "Habitaciones y Dormitorios",
    aerialLabel: "🌉 Vista Aérea del Centro de Kyiv",
    step1of3: "Paso 1 de 3",
    step2of3: "Paso 2 de 3",
    step3of3: "Paso 3 de 3",
    lblGuestFullName: "Nombre Completo",
    lblGuestEmailAddress: "Dirección de Correo",
    lblGuestNumber: "Número de Huéspedes",
    lblGuestSingular: "Huésped",
    lblGuestPlural: "Huéspedes",
    lblDailyRate: "Tarifa Diaria:",
    lblSelectedRange: "Período:",
    lblEstimatedTotal: "Total Estimado:",
    lblNights: "noches",
    lblSelected: "Seleccionado",
    lblInRange: "En Rango",
    lblSelectionPreview: "Vista Previa",
    lblBooked: "Reservado",
    widgetTitle: "Idioma del sitio web",
    translatorTitle: "Traducción del sitio",
    changeAppLang: "Cambiar idioma de la aplicación",
    placeholderTranslate: "Escriba una frase para traducir...",
    translationResult: "Resultado de la traducción",
    copy: "Copiar",
    copied: "Copiado",
    translatingProgress: "Generando traducción perfecta...",
    btnTranslate: "Traducir",
    btnTranslating: "Traduciendo...",
    phrasebook: "Guía de frases del hostal",
    from: "De",
    to: "A",
    lblChooseCheckIn: "Por favor, interactúe con el calendario para elegir su fecha de entrada.",
    lblChooseCheckOut: "¡Excelente! Ahora, seleccione su fecha de salida.",
    lblPaymentMethod: "Seleccionar Método de Pago",
    payOnlineLiqPay: "Pago Seguro en Línea LiqPay",
    payOnlineDetails: "Garantía instantánea • Tarjeta / Privat24",
    payOnlineDetailsForm: "Garantía instantánea • Visa / Mastercard / Privat24",
    payAtReception: "Pagar en Recepción",
    payAtReceptionDetails: "Efectivo o tarjeta a la llegada",
    payAtReceptionDetailsForm: "Sin prepago con tarjeta • Pague en recepción",
    btnConfirmReservation: "Confirmar Reserva",
    btnProceedToCheckout: "Proceder al Pago",
    lblContactPhone: "Número de Teléfono de Contacto",
    lblPhonePlaceholder: "ej. 50 123 4567",
    lblSecureHotelReservation: "Reserva de Hotel Segura",
    lblCheckoutReady: "Listo para salir",
    lblReservationConfirmedHeader: "RESERVA CONFIRMADA",
    lblCongratulations: "¡Felicitaciones",
    lblReservedSafely: "está reservado de forma segura.",
    lblConfirmationId: "ID de Confirmación:",
    lblCheckInDate: "Fecha de Check-In:",
    lblCheckOutDate: "Fecha de Check-Out:",
    lblPaidTransaction: "Transacción Pagada:",
    lblTotalSettled: "Total Liquidado:",
    lblReceptionTip: "¡Presente esta ID de confirmación o el comprobante de pago en el mostrador de recepción a su llegada! El lobby está abierto las 24 horas, los 7 días de la semana.",
    lblReserveRoom: "Reservar",
    lblSecureCheckout: "Pago Seguro",
    lblReceiptVoucher: "Cupón de Recibo",
    lblGuestRepresentative: "Representante del Huésped:",
    lblReservedRoom: "Habitación Reservada:",
    lblCheckInIntake: "Entrada de Check-In:",
    lblCheckOutDeparture: "Salida de Check-Out:",
    lblAuthorizedGuests: "Huéspedes Autorizados:",
    lblGatewayTransactionRef: "Ref. de Transacción de Pasarela:",
    lblReceptionTipModal: "Presente su cupón de confirmación o el nombre de su reserva en el lobby del albergue al registrarse. La recepción está abierta las 24 horas, los 7 días de la semana.",
    lblShowLess: "ver menos",
    lblMore: "más",
    lblCapacity: "Capacidad",
    lblBedsLeft: "camas restantes",
    lblBedLeft: "cama restante",
    bookingConfirmedTitle: "Reserva Aprobada y Confirmada",
    bookingConfirmedDesc: "¡Felicitaciones! Su transacción está asegurada y verificada. Su alojamiento en Kyiv está reservado.",
    btnReturnExplore: "Volver al Inicio",
    aboutText: "Cozzydine es una empresa ucraniana fundada en 2020, especializada en el sector crucial del alojamiento dentro de la industria hotelera, dedicada a ofrecer hospedajes temporales de calidad que atienden de manera fluida tanto a viajeros nacionales como internacionales.\n\nHostel Cozzydine es nuestro nuevo proyecto diseñado para ofrecer alojamiento accesible para una amplia gama de huéspedes, tales como mochileros, autostopistas, parejas, creadores remotos, migrantes urbanos, exploradores en grupo y viajeros de negocios por igual.\n\nUbicada en el centro de Kyiv, nuestra propiedad está minuciosamente diseñada para ofrecer un alojamiento seguro y bien conectado, equipado con todos los servicios esenciales para una estadía cómoda, un descanso reparador y una conexión seleccionada a los inspiradores centros de arte y resiliencia cultural de Kyiv.",
    sightseeingTag: "Guía Turística Interactiva",
    sightseeingTitle: "Centros de Arte, Cultura y Resiliencia de Kyiv",
    sightseeingDesc: "Hostel Cozzydine se beneficia de la proximidad a pie de conexiones de metro que le llevan directamente al centro de Kyiv. Explore algunos puntos de interés local:",
    sight1Name: "Khreshchatyk y Maidan",
    sight1Desc: "La histórica plaza de la ciudad, rodeada de cafés, plazas monumentales y artistas callejeros vibrantes.",
    sight1Meters: "9 min en Metro",
    sight2Name: "Pequeña Lavra de Kyiv",
    sight2Desc: "Espectaculares cúpulas doradas de siglos de antigüedad, bosques monásticos y cuevas subterráneas sagradas.",
    sight2Meters: "10 min en coche",
    sight3Name: "Catedral de Santa Sofía y Puerta de Oro",
    sight3Desc: "Una impresionante catedral del siglo XI declarada patrimonio de la UNESCO, junto con las ruinas de la antigua puerta medieval de Kyiv.",
    sight3Meters: "12 min en Metro",
    sight4Name: "Cuesta de San Andrés y Podil",
    sight4Desc: "La calle artística y bohemia de Kyiv, llena de galerías, mercados de artesanías y cafeterías acogedoras.",
    sight4Meters: "15 min en Metro",
    sight5Name: "Iglesia de San Nicolás",
    sight5Desc: "Impresionante obra maestra del neogótico diseñada por Vladyslav Gorodetsky, sede de la sala de música de órgano.",
    sight5Meters: "4 min en coche",
    sight6Name: "Monumento a la Patria (Rodyna-Mat)",
    sight6Desc: "La estatua monumental de acero más alta de Ucrania sobre el río Dnipro, que simboliza la resiliencia y el orgullo nacional.",
    sight6Meters: "10 min en coche",
    sight7Name: "Colina y Parque Volodymyrskyy",
    sight7Desc: "Un parque tranquilo en la colina con vistas al río Dnipro, que alberga un monumento histórico y un puente peatonal panorámico.",
    sight7Meters: "12 min en coche",
    checklistTitle: "Lista de Verificación de Tránsito",
    checklistSpot2: "Estación de metro de Lybidska",
    checklistSpotBus: "Estación de autobuses de Lybidska",
    checklistSpotCentralBus: "Estación Central de Autobuses de Kyiv",
    checklistSpotRailway: "Estación Central de Trenes de Kyiv",
    checklistSpot3: "Centro de Kyiv",
    checklistWalkTip: "Entre directamente al patio trasero del edificio 75a, ¡los timbres están claramente marcados Cozzydine!",
    physicalEndpointLabel: "DIRECCIÓN FÍSICA Y CONTACTOS",
    directChatsLabel: "CHATS DIRECTOS Y REDES SOCIALES",
    workingHoursLabel: "Horario de Atención:",
    workingHoursValue: "24/7 (Siempre Abierto)",

    // Room Details
    room_type_private: "Habitación privada",
    room_type_dorm: "Dormitorio compartido",
    lblLongStayOfferTitle: "Oferta de larga estancia",
    lblLongStayOfferDesc: "Reserve 3+ noches para un 10% de descuento, o 7+ noches para un 15%.",
    "room_double-room_name": "Habitación Doble",
    "room_double-room_desc": "Cómoda habitación privada con una cama grande, perfecta para dos huéspedes. Incluye Wi-Fi gratuito, sábanas limpias y comodidades estándar para una estancia relajante.",
    "room_eight-bed-dorm_name": "Dormitorio de 8 Camas",
    "room_eight-bed-dorm_desc": "Habitación compartida económica con ocho cómodas literas. Incluye Wi-Fi gratuito, casilleros seguros personales, tomacorrientes y acceso a baño compartido y cocina pequeña.",
    "room_six-bed-dorm_name": "Dormitorio de 6 Camas",
    "room_six-bed-dorm_desc": "Acogedora habitación compartida con seis cómodas literas. Incluye Wi-Fi gratuito, casilleros personales, enchufes y acceso completo a áreas comunes.",
    "room_four-bed-womens-dorm_name": "Dormitorio Femenino de 4 Camas",
    "room_four-bed-womens-dorm_desc": "Acogedor dormitorio compartido para mujeres con cuatro literas. Incluye Wi-Fi de alta velocidad, casilleros propios, tomas de corriente y acceso a baño y cocina.",
    "room_four-bed-mens-dorm_name": "Dormitorio Masculino de 4 Camas",
    "room_four-bed-mens-dorm_desc": "Acogedor dormitorio compartido para hombres con cuatro cómodas literas. Incluye Wi-Fi, casilleros de seguridad, enchufes y acceso a kitchenette.",

    // Interactive Transit & Local guide tabs
    tabLybidska: "Metro Lybidska Salida 2 (< 2 min)",
    tabBus: "Parada de autobús Lybidska (< 2 min)",
    tabPalats: "Centro de Kyiv (Maidan Nezalezhnosti)",
    tabTrain: "Estación Central (10 min)",
    tabCentralBus: "Estación Central de Autobuses (3 min)",

    // Metro Access & Transit Box
    metroAccessTitle: "🚇 Acceso al Metro y Tránsito",
    metroAccessDesc: "El Hostal Cozzydine Kyiv es increíblemente fácil de llegar en transporte público. Estamos ubicados a un breve paseo de las líneas metropolitanas más importantes:",
    metroAccessItem1: "Estación de metro Lybidska (Salida 2) — menos de 2 minutos a pie",
    metroAccessItem2: "Centro de Kyiv (Estación Maidan Nezalezhnosti) — 10 min de trayecto (5.3 km)",
    metroAccessItem3: "Estación Central de Kyiv — 10 minutos en metro",

    // Rooms Subtitle
    roomsSubtitle: "Habitaciones privadas cómodas y dormitorios compartidos sociables diseñados para cada viajero. Encuentre su espacio perfecto, disfrute de comodidades premium y conéctese con facilidad.",

    // Testimonials
    testimonialsTag: "💬 Testimonios de Huéspedes",
    testimonialsTitle: "Experiencias de Huéspedes",
    testimonialsDesc: "Comentarios e historias reales y autenticadas de nuestros mochileros, viajeros en grupo, viajeros de negocios y nómadas digitales.",
    reviewsTitle: "Lo que dicen nuestros huéspedes",

    // Guest Review Translations
    "rev_REV-01_content": "Excelente ubicación, justo al lado del metro y del centro comercial Ocean Plaza. Limpio, ordenado y el dueño siempre está en el lugar las 24 horas, los 7 de la semana por si tienes preguntas o problemas. Recomiendo altamente el hostal Cozzydine. Volveré aquí.",
    "rev_REV-01_tag1": "Cerca del Metro",
    "rev_REV-01_tag2": "Ocean Plaza",
    "rev_REV-01_tag3": "Centro Accesible",
    "rev_REV-01_tag4": "Soporte 24/7",
    "rev_REV-02_content": "El hostal está muy bien para el precio, todo está impecable, las camas son comodísimas. Y el anfitrión es amable y atento. ¡Lo recomiendo de verdad!",
    "rev_REV-02_tag1": "Buen Precio",
    "rev_REV-02_tag2": "Súper Limpio",
    "rev_REV-02_tag3": "Camas Cómodas",
    "rev_REV-02_tag4": "Anfitrión Amable",
    "rev_REV-03_content": "El anfitrión es muy meticuloso en mantener el lugar ordenado y responde muy rápido a cualquier mensaje.",
    "rev_REV-03_tag1": "Host Meticuloso",
    "rev_REV-03_tag2": "Siempre Limpio",
    "rev_REV-03_tag3": "Muy Atento",
    "rev_REV-04_content": "Me encantó la comunicación conmigo al no ser local; siempre estuvieron disponibles y explicaron a la perfección cómo llegar. El hostal es limpio y ordenado, el colchón cómodo con sábanas limpias. Hay ducha y cocina. Es un establecimiento en el sótano, por lo que puedes esperar a que pasen las alertas de forma protegida.",
    "rev_REV-04_tag1": "Gran Comunicación",
    "rev_REV-04_tag2": "Limpio y Ordenado",
    "rev_REV-04_tag3": "Refugio Seguro",
    "rev_REV-04_tag4": "Cama Cómoda",
    "rev_REV-05_content": "He estado aquí por segunda vez y volver a hospedarme fue un gran placer. Chinju es un anfitrión estupendo y profesional. Tuve problemas de vuelo y llegué tardísimo, pero la entrada tardía no fue inconveniente. La habitación estaba muy limpia y el balcón es fantástico. Muy bien ubicado.",
    "rev_REV-05_tag1": "Huésped Frecuente",
    "rev_REV-05_tag2": "Host Profesional",
    "rev_REV-05_tag3": "Balcón Estupendo",
    "rev_REV-05_tag4": "Barrio Cultural",
    "rev_REV-06_content": "¡Me encantó este lugar! Principalmente por la fantástica ubicación, las maravillosas vistas y el precio imbatible. Elegante y sencillo. Reservé para dos noches y me quedé dos semanas. Se siente un espacio tan agradable que no quieres irte.",
    "rev_REV-06_tag1": "Estadía Extendida",
    "rev_REV-06_tag2": "Vistas Bellas",
    "rev_REV-06_tag3": "Sencillo y Elegante",
    "rev_REV-06_tag4": "Mejor Precio",
    "rev_REV-07_content": "El Hotel Cozzydine fue el lugar idóneo para calmarnos y relajarnos tras una experiencia de viaje estresante en Kyiv. Teníamos vistas increíbles, el edificio tiene encanto, el apartamento estaba impecable y la suite de baño es grande y hermosa. Chinju se portó increíble.",
    "rev_REV-07_tag1": "Santuario Ideal",
    "rev_REV-07_tag2": "Piso Impecable",
    "rev_REV-07_tag3": "Baño Grande",
    "rev_REV-07_tag4": "Excelentes Vistas",
    "rev_REV-08_content": "Fue como vivir en un cuento de hadas hospedarse en el bello Hotel Cozzydine. Gracias Chinju por estar siempre disponible las 24 horas y ser tan buen anfitrión. ¡Sin duda me volveré a hospedar aquí en el futuro!",
    "rev_REV-08_tag1": "Ambiente Mágico",
    "rev_REV-08_tag2": "Atención 24/7",
    "rev_REV-08_tag3": "Anfitrión Estelar",
    "rev_REV-08_tag4": "Decoración Bella",
    perNight: "/ noche",
    bedsAvailable: "camas disponibles",
    bedAvailable: "cama disponible",
    longStayDiscount: "¡Descuento de larga estancia aplicado!",
    lblPrivacyPolicy: "Política de privacidad",
    lblAllRightsReserved: "Todos los derechos reservados.",
    lblPreparingBookingEngine: "Preparando el motor de reservas...",
    lblLoadingMap: "Cargando el mapa de ubicación de Cozzy...",
    lblEmail: "Correo electrónico:",
    lblCallFrontDesk: "Llamar (Recepción):",
    lblHostel: "Hostal",
    lblTransit250m: "250 metros",
    lblTransit300m: "300 metros",
    lblTransit3m: "3 min en coche",
    lblTransit10m: "10 min en coche",
    lblCozzyAiAssistant: "Cozy AI",
    lblCozzyCoreAi: "Cozy Core AI"
  },
  fr: {
    navHome: "Accueil",
    navRooms: "Chambres & Dortoirs",
    navAbout: "À Propos",
    navContact: "Contact",
    btnBook: "Réserver",
    heroRating: "★ 4.9 Expérience Boutique Exceptionnelle",
    heroTitlePart1: "Auberge chaleureuse",
    heroTitlePart2: "au centre de Kyiv",
    heroSubtitle: "Bienvenue à l'Auberge Cozzydine, un hébergement calme, sûr et bien situé en ville. Avec des chambres privées confortables et des dortoirs partagés parfaits pour les voyageurs en solo, les couples, les créateurs à distance, les explorateurs en groupe et les professionnels.",
    heroBtnBook: "Réserver & Économiser",
    heroBtnMap: "Guider des Alentours",
    heroBtnContact: "CONTACTEZ-NOUS",
    amenitiesLabel: "SERVICES HAUT DE GAMME",
    amenitiesTitle: "Équipements et Services",
    amenitiesDesc: "Équipements sélectionnés offrant le confort de voyage essentiel, des espaces de vie sécurisés et des zones Internet haut débit.",
    amWifi: "Wi-Fi haut débit gratuit",
    amSecurity: "Surveillance par caméra (CCTV) et sécurité",
    amAc: "Climatisation/Ventilation",
    amKitchen: "Cuisine Entièrement Équipée",
    amWater: "Approvisionnement continu en eau chaude",
    amLuggage: "Consigne à bagages",
    amLockers: "Casiers sécurisés",
    amLaundry: "Laverie en libre-service",
    amReception: "Réception ouverte 24h/24",
    promoTag: "SUPPORT & CONFIANCE",
    promoTitle: "Découvrez le Patrimoine de l'Ukraine",
    promoDesc: "Kyiv est une capitale dynamique pleine d'art, de culture et de courage. Reposez-vous sereinement chez Cozzydine grâce à notre abri sécurisé et notre excellente communication.",
    promoBtn: "Réserver mon Lit",
    aboutTitlePart1: "Hospitalité sur Mesure",
    aboutTitlePart2: "Depuis 2020",
    aboutQuarterTitle: "KYIV COZZYDINE HQ",
    aboutQuarterDesc: "Géré directement par Chinju Ravi et l'équipe d'accueil de Kyiv avec assistance multilingue.",
    contactTag: "TOUJOURS DISPONIBLE",
    contactTitle: "Contacter Cozzydine",
    contactSubtitle: "Contactez-nous pour des demandes de réservation personnalisées, une assistance à l'arrivée ou des conseils d'itinéraire.",
    contactFormTitle: "Écrivez-nous un Message",
    contactSubmittedTitle: "Message Envoyé en Toute Sécurité",
    contactSubmittedDesc: "Merci! L'équipe Cozzydine ou notre hôte virtuel Cozy AI vous répondront d'ici 15 minutes.",
    contactLblName: "Votre Nom Complet",
    contactLblEmail: "Adresse E-mail",
    contactLblMsg: "Votre Message ou Demande",
    contactBtnSubmit: "Envoyer le Message",

    roomType: "Type de Chambre",
    // Additional standard baseline keys
    home: "Accueil",
    rooms: "Chambres & Dortoirs",
    about: "À Propos",
    contact: "Contact",
    guestName: "Nom du client",
    guestEmail: "Adresse e-mail",
    checkIn: "Date d'arrivée",
    checkOut: "Date de départ",
    guests: "Voyageurs",
    submit: "Envoyer",
    successMessage: "Envoyé avec succès !",
    close: "Fermer",
    open: "Ouvrir",
    learnMore: "En savoir plus",
    backToStart: "Retour au début",
    policiesTitle: "Règlement Intérieur & Politiques",
    policyCatAll: "Toutes les politiques",
    policyCatStay: "Arrivée & Séjour",
    policyCatFacilities: "Installations & Espaces",
    policyCatRules: "Règlement intérieur",
    policiesSubtitle: "Pour garantir un séjour paisible, sûr et très confortable à tous nos clients, veuillez consulter nos directives de fonctionnement et d'éthique.",
    policyCheckInOutTitle: "Arrivée / Départ",
    policyCheckInOutDesc: "Check-in: 14h00-23h00. Check-out: avant 11h00. L'enregistrement anticipé et le départ tardif sont soumis à disponibilité, notification préalable et approbation.",
    policyQuietTitle: "Heures de Silence",
    policyQuietDesc: "Les heures de calme s'appliquent dans toutes les chambres et espaces communs de 23h00 à 08h00.",
    policyPaymentTitle: "Politique d'Identité, Paiement & Âge",
    policyPaymentDesc: "Nous préférons les paiements par carte, et une pièce d'identité officielle valide avec photo ou un passeport est nécessaire pour l'enregistrement. Veuillez noter qu'un âge minimum de 18 ans est requis pour tout hébergement en dortoir.",
    policyCancellationTitle: "Politique d'Annulation",
    policyCancellationDesc: "• Tarifs Standard : Les annulations ou modifications sont gratuites avec un préavis jusqu'à 48 heures avant l'arrivée ; les modifications tardives entraînent des frais d'annulation équivalents à une nuit.\n\n• Tarifs Non Remboursables : Le montant total de la réservation est débité lors de la réservation et est strictement non remboursable et non modifiable.\n\n• Non-présentations (No-Shows) : L'absence sans notification préalable entraîne l'annulation immédiate de la réservation et la facturation de la totalité du séjour.",
    policyLuggageTitle: "Bagagerie",
    policyLuggageDesc: "Nous offrons une consigne à bagages gratuite pour une récupération le jour même, disponible à la fois avant l'enregistrement et après le départ. Veuillez demander ce service à l'avance ou en informer la réception avant de laisser vos bagages.",
    policyLaundryTitle: "Laverie",
    policyLaundryDesc: "Espace libre-service disponible sur place. Le coût par lavage est de 100 UAH, veuillez vérifier et confirmer la disponibilité auprès de la réception. Ne suspendez pas de linge mouillé ou de serviettes dans les dortoirs, utilisez la salle de séchage désignée.",
    policyComfortTitle: "Alcool, Drogues & Tabac",
    policyComfortDesc: "La possession, la distribution ou la consommation d'alcool et de substances illégales sont strictement interdites ; fumer et vapoter sont entièrement interdits dans l'auberge.",
    policyShelterTitle: "Sécurité & Abri",
    policyShelterDesc: "L'auberge est située au rez-de-chaussée et au sous-sol, la station de métro se trouve également à proximité.",
    policyVisitorsTitle: "Pas de Visiteurs",
    policyVisitorsDesc: "Accès interdit aux personnes n'ayant pas de réservation.",
    policyFoodTitle: "Savoir-vivre en Dortoir",
    policyFoodDesc: "Il est interdit de manger ou de conserver de la nourriture et des boissons ouvertes dans les dortoirs afin de maintenir la propreté et d'éviter les nuisibles. Veuillez ranger vos bagages et effets personnels dans le casier qui vous est attribué ou sous votre lit. Ne dispersez pas vos affaires sur le sol, dans les couloirs ou sur des lits non attribués.",
    policyKitchenTitle: "Toilettes & Kitchenette",
    policyKitchenDesc: "Lavez et rangez tous les ustensiles de cuisine immédiatement après utilisation. Tirez toujours la chasse d'eau des toilettes, essuyez les surfaces et passez la serpillière avant de sortir.",
    policyLockersTitle: "Casiers Sécurisés",
    policyLockersDesc: "Des casiers individuels autoportants sont disponibles dans les dortoirs de l'auberge pour sécuriser les effets personnels.",
    policyWorkspaceTitle: "Wi-Fi haut débit gratuit",
    policyWorkspaceDesc: "Oui, un accès Wi-Fi gratuit et haut débit est disponible dans toute l'auberge.",
    exploreRooms: "Explorer les Chambres",
    galleryLabel: "Visite visuelle",
    galleryTitle: "Galerie de l'Auberge",
    gallerySubtitle: "Faites un voyage visuel à travers les coins chaleureux de Cozzydine, ses espaces sociaux de qualité et ses dortoirs calmes.",
    galleryAlt_1: "Chambre double, Hostel Cozzydine, Kiev",
    galleryAlt_2: "Couloir et intérieur, Hostel Cozzydine, Kiev",
    galleryAlt_3: "Réception et hall, Hostel Cozzydine, Kiev",
    galleryAlt_4: "Chambre double privée, Hostel Cozzydine, Kiev",
    galleryAlt_5: "Chambre double, Hostel Cozzydine, Kiev",
    galleryAlt_6: "Dortoir 4 lits, Hostel Cozzydine, Kiev",
    galleryAlt_7: "Dortoir 6 lits, Hostel Cozzydine, Kiev",
    stampGalleryBadge: "Édition Philatélique de Kiev",
    lightboxPostage: "Collection d'Archives de Kiev",
    slide1Title: "Salon Commun Chaleureux",
    slide1Desc: "L'endroit idéal pour travailler, lire, jouer ou faire des rencontres.",
    slide2Title: "Lits de Dortoir Confortables",
    slide2Desc: "Matelas orthopédiques, prises individuelles, liseuses et rideaux occultants pour un sommeil réparateur.",
    slide3Title: "Chambre Double Florale Douillette",
    slide3Desc: "Une chambre double lumineuse et soignée avec une literie fleurie violette et blanche caractéristique, un miroir mural élégant, un portemanteau et une commode basse.",
    slide4Title: "Kitchenette Commune",
    slide4Desc: "Équipée d'un micro-ondes, bouilloire, grille-pain et ustensiles. Idéal pour un encas et faire des rencontres.",
    slide5Title: "Chambre lits jumeaux Premium",
    slide5Desc: "Un sanctuaire privé et paisible avec du linge de maison luxueux, un espace de travail et une déco soignée.",
    slide6Title: "Entrée Charmante Côté Cour",
    slide6Desc: "Notre cour intérieure calme aux couleurs noir et blanc, à deux pas de Lybidska.",
    filterAll: "Tout",
    filterPrivate: "Privé",
    filterDorms: "Dortoirs",
    directionsTitle: "Directions & Guide de Transport",
    directionsSubtitle: "Comment rejoindre facilement l'auberge Cozzydine depuis le métro et les gares.",
    transitLybidskaLabel: "Métro Lybidska Sortie 2 (< 2 min)",
    transitBus: "Arrêt de bus Lybidska (< 2 min)",
    transitPalatsLabel: "Centre-ville de Kyiv (Maidan Nezalezhnosti)",
    transitTrainLabel: "Gare Centrale (10 min)",
    monumentLabel: "🏛️ Monument de l'Indépendance, Kyiv",
    roomsAndDorms: "Chambres & Dortoirs",
    aerialLabel: "🌉 Vue Aérienne du Centre-ville de Kyiv",
    step1of3: "Étape 1 sur 3",
    step2of3: "Étape 2 sur 3",
    step3of3: "Étape 3 sur 3",
    lblGuestFullName: "Nom Complet",
    lblGuestEmailAddress: "Adresse E-mail",
    lblGuestNumber: "Nombre de Voyageurs",
    lblGuestSingular: "Voyageur",
    lblGuestPlural: "Voyageurs",
    lblDailyRate: "Tarif Journalier :",
    lblSelectedRange: "Période :",
    lblEstimatedTotal: "Total Estimé :",
    lblNights: "nuits",
    lblSelected: "Sélectionné",
    lblInRange: "Dans l'Intervalle",
    lblSelectionPreview: "Aperçu",
    lblBooked: "Réservé",
    widgetTitle: "Langue du site",
    translatorTitle: "Traduction du site",
    changeAppLang: "Changer la langue de l'application",
    placeholderTranslate: "Saisissez une phrase à traduire...",
    translationResult: "Résultat de la traduction",
    copy: "Copier",
    copied: "Copié",
    translatingProgress: "Génération de la traduction parfaite...",
    btnTranslate: "Traduire",
    btnTranslating: "Traduction...",
    phrasebook: "Guide de phrases de l'auberge",
    from: "De",
    to: "Vers",
    lblChooseCheckIn: "Veuillez interagir avec le calendrier pour choisir votre date d'arrivée.",
    lblChooseCheckOut: "Parfait ! Sélectionnez maintenant votre date de départ.",
    lblPaymentMethod: "Sélectionner le Mode de Paiement",
    payOnlineLiqPay: "Paiement Sécurisé en Ligne LiqPay",
    payOnlineDetails: "Garantie instantanée • Carte / Privat24",
    payOnlineDetailsForm: "Garantie instantanée • Visa / Mastercard / Privat24",
    payAtReception: "Payer à la Réception",
    payAtReceptionDetails: "Espèces ou carte à l'arrivée",
    payAtReceptionDetailsForm: "Pas de prépaiement par carte • Payez à la réception",
    btnConfirmReservation: "Confirmer la Réervation",
    btnProceedToCheckout: "Passer à la Caisse",
    lblContactPhone: "Numéro de Téléphone",
    lblPhonePlaceholder: "ex. 50 123 4567",
    lblSecureHotelReservation: "Réservation d'Hôtel Sécurisée",
    lblCheckoutReady: "Prêt pour le paiement",
    lblReservationConfirmedHeader: "RÉSERVATION CONFIRMÉE",
    lblCongratulations: "Félicitations",
    lblReservedSafely: "est réservé en toute sécurité.",
    lblConfirmationId: "ID de Confirmation :",
    lblCheckInDate: "Date d'arrivée :",
    lblCheckOutDate: "Date de départ :",
    lblPaidTransaction: "Transaction Payée :",
    lblTotalSettled: "Total Réglé :",
    lblReceptionTip: "Présentez cet ID de confirmation ou le reçu de paiement à la réception à votre arrivée ! Le hall est ouvert 24h/24 et 7j/7.",
    lblReserveRoom: "Réserver",
    lblSecureCheckout: "Paiement Sécurisé",
    lblReceiptVoucher: "Bon de Reçu",
    lblGuestRepresentative: "Représentant du Client :",
    lblReservedRoom: "Chambre Réservée :",
    lblCheckInIntake: "Heure d'Arrivée :",
    lblCheckOutDeparture: "Heure de Départ :",
    lblAuthorizedGuests: "Clients Autorisés :",
    lblGatewayTransactionRef: "Réf. de Transaction Passerelle :",
    lblReceptionTipModal: "Présentez votre bon de confirmation ou le nom de votre réservation à la réception lors de votre arrivée. La réception est ouverte 24/7.",
    lblShowLess: "voir moins",
    lblMore: "plus",
    lblCapacity: "Capacité",
    lblBedsLeft: "lits restants",
    lblBedLeft: "lit restant",
    bookingConfirmedTitle: "Réservation Approuvée & Confirmée",
    bookingConfirmedDesc: "Félicitations ! Votre transaction est sécurisée et vérifiée. Votre hébergement à Kyiv est entièrement réservé.",
    btnReturnExplore: "Retour à l'Accueil",
    aboutText: "Cozzydine est une entreprise ukrainienne créée en 2020, spécialisée dans le secteur essentiel de l'hébergement au sein de l'industrie hôtelière, dédiée à la fourniture de logements temporaires de qualité destinés aux clients nationaux et internationaux.\n\nL'Hostel Cozzydine est notre nouvelle initiative visant à offrir un hébergement abordable à un large éventail de voyageurs, tels que les routards, les auto-stoppeurs, les couples, les créateurs à distance, les migrants urbains, les explorateurs en groupe et les professionnels.\n\nSitué au centre-ville de Kyiv, notre établissement est minutieusement conçu pour offrir un hébergement sûr, sécurisé et bien desservi, équipé de tous les services essentiels pour un séjour confortable, un sommeil réparateur, ainsi qu'un accès privilégié aux centres artistiques et aux pôles de résilience culturelle inspirants de Kyiv.",
    sightseeingTag: "Guide Touristique Interactif",
    sightseeingTitle: "Les Centres d'Art, de Culture & de Résilience de Kyiv",
    sightseeingDesc: "L'Hostel Cozzydine bénéficie d'une proximité directe à pied avec les lignes de métro menant au centre de Kyiv. Découvrez les sites d'intérêt locaux :",
    sight1Name: "Khreshchatyk & Maïdan",
    sight1Desc: "La place historique de la ville bordée de cafés en plein air, de monuments emblématiques et d'artistes de rue.",
    sight1Meters: "9 min en métro",
    sight2Name: "Monastère des Grottes de Kyiv",
    sight2Desc: "De magnifiques coupoles dorées séculaires, des forêts monastiques et des galeries souterraines sacrées.",
    sight2Meters: "10 min de trajet",
    sight3Name: "Cathédrale Sainte-Sophie & Porte Dorée",
    sight3Desc: "Une cathédrale époustouflante du XIe siècle classée à l'UNESCO, accompagnée des ruines de l'ancienne porte médiévale de Kyiv.",
    sight3Meters: "12 min en métro",
    sight4Name: "Descente Saint-André & Podil",
    sight4Desc: "La rue artistique bohème de Kyiv regorgeant de galeries, de marchés artisanaux et de cafés chaleureux.",
    sight4Meters: "15 min en métro",
    sight5Name: "Église Saint-Nicolas de Kyiv",
    sight5Desc: "Impressionnant chef-d'œuvre néo-gothique conçu par Vladyslav Gorodetsky et abritant la salle de concert d'orgue.",
    sight5Meters: "4 min de trajet",
    sight6Name: "Monument de la Patrie (Rodyna-Mat)",
    sight6Desc: "La plus haute statue monumentale en acier d'Ukraine surplombant le Dnipro, symbole de résilience et de fierté nationale.",
    sight6Meters: "10 min de trajet",
    sight7Name: "Colline & Parc Volodymyrskyy",
    sight7Desc: "Un parc verdoyant sur une colline surplombant le Dnipro, doté d'un monument historique et d'une passerelle piétonne panoramique.",
    sight7Meters: "12 min de trajet",
    checklistTitle: "Checklist de Transit Backpacker",
    checklistSpot2: "Station de métro Lybidska",
    checklistSpotBus: "Gare routière de Lybidska",
    checklistSpotCentralBus: "Gare routière centrale de Kyiv",
    checklistSpotRailway: "Gare ferroviaire centrale de Kyiv",
    checklistSpot3: "Centre de Kyiv",
    checklistWalkTip: "Entrez directement dans la cour intérieure 75a, les sonnettes sont clairement étiquetées Cozzydine !",
    physicalEndpointLabel: "ADRESSE PHYSIQUE & DETAILS",
    directChatsLabel: "CHATS DIRECTS & RESEAUX SOCIAUX",
    workingHoursLabel: "Heures d'Ouverture :",
    workingHoursValue: "24/7 (Toujours Ouvert)",

    // Room Details
    room_type_private: "Chambre privée",
    room_type_dorm: "Dortoir partagé",
    lblLongStayOfferTitle: "Offre Long Séjour",
    lblLongStayOfferDesc: "Réservez 3+ nuits pour 10% de réduction, ou 7+ nuits pour 15%.",
    "room_double-room_name": "Chambre Double",
    "room_double-room_desc": "Chambre privée confortable avec un grand lit double, parfaite pour deux personnes. Comprend Wi-Fi gratuit, draps frais et équipements standard pour un séjour relaxant.",
    "room_eight-bed-dorm_name": "Dortoir à 8 Lits",
    "room_eight-bed-dorm_desc": "Dortoir économique doté de huit lits superposés confortables. Comprend Wi-Fi gratuit, casiers individuels sécurisés, prises de courant et accès aux sanitaires et kitchenette communs.",
    "room_six-bed-dorm_name": "Dortoir à 6 Lits",
    "room_six-bed-dorm_desc": "Dortoir chaleureux doté de six lits superposés confortables. Comprend Wi-Fi gratuit, casiers sécurisés, prises électriques et accès complet à la cuisine commune.",
    "room_four-bed-womens-dorm_name": "Dortoir Féminin à 4 Lits",
    "room_four-bed-womens-dorm_desc": "Dortoir confortable réservé aux femmes, doté de quatre lits superposés. Comprend connexion Wi-Fi, casiers personnels, prises et accès aux sanitaires.",
    "room_four-bed-mens-dorm_name": "Dortoir Masculin à 4 Lits",
    "room_four-bed-mens-dorm_desc": "Dortoir chaleureux réservé aux hommes, doté de quatre lits superposés. Comprend Wi-Fi haut débit, casiers sécurisés, prises de courant et accès kitchenette.",

    // Interactive Transit & Local guide tabs
    tabLybidska: "Métro Lybidska Sortie 2 (< 2 min)",
    tabBus: "Arrêt de bus Lybidska (< 2 min)",
    tabPalats: "Centre-ville de Kyiv (Maidan Nezalezhnosti)",
    tabTrain: "Gare Centrale (10 min)",
    tabCentralBus: "Gare Routière Centrale (3 min)",

    // Metro Access & Transit Box
    metroAccessTitle: "🚇 Accès Métro & Transit",
    metroAccessDesc: "L'Hostel Cozzydine Kyiv est incroyablement facile d'accès en transports en commun. Nous sommes à quelques pas seulement des principales lignes de métro :",
    metroAccessItem1: "Station de métro Lybidska (Sortie 2) — à moins de 2 minutes à pied",
    metroAccessItem2: "Centre-ville de Kyiv (Station Maidan Nezalezhnosti) — 10 min de trajet (5,3 km)",
    metroAccessItem3: "Gare Centrale de Kyiv (Kyiv-Passajyrsky) — 10 minutes en métro",

    // Rooms Subtitle
    roomsSubtitle: "Des chambres privées confortables et des dortoirs partagés chaleureux conçus pour chaque voyageur. Trouvez votre espace idéal, profitez d'équipements de qualité et connectez-vous facilement.",

    // Testimonials
    testimonialsTag: "💬 Témoignages des Clients",
    testimonialsTitle: "Expériences de nos clients",
    testimonialsDesc: "Vrais retours d'expérience et témoignages vérifiés de nos routards, voyageurs en groupe, voyageurs d'affaires et nomades digitaux.",
    reviewsTitle: "Ce que disent nos clients",

    // Guest Review Translations
    "rev_REV-01_content": "Super emplacement, juste à côté du métro et du centre commercial Ocean Plaza. Propre et bien tenu et le propriétaire est disponible 24h/24 et 7j/7 sur place pour toute question. Si vous cherchez un hébergement abordable au centre de Kyiv, je vous recommande vivement l'Hostel Cozzydine. J'y séjournerai à nouveau.",
    "rev_REV-01_tag1": "Proche Métro",
    "rev_REV-01_tag2": "Ocean Plaza",
    "rev_REV-01_tag3": "Centre Abordable",
    "rev_REV-01_tag4": "Assistance 24/7",
    "rev_REV-02_content": "L'auberge est impeccable pour le prix proposé, tout est propre, les lits sont extrêmement confortables. De plus, l'hôte est très sympathique et serviable. Je recommande vivement !",
    "rev_REV-02_tag1": "Excellent Prix",
    "rev_REV-02_tag2": "Super Propre",
    "rev_REV-02_tag3": "Lits Confortables",
    "rev_REV-02_tag4": "Hôte Aimable",
    "rev_REV-03_content": "L'hôte est extrêmement consciencieux quant à la propreté de l'établissement et se montre particulièrement réactif à tous nos besoins.",
    "rev_REV-03_tag1": "Hôte Meticuleux",
    "rev_REV-03_tag2": "Toujours Propre",
    "rev_REV-03_tag3": "Très Réactif",
    "rev_REV-04_content": "J'ai adoré la communication de l'équipe envers la voyageuse non-locale que je suis ; ils ont constamment gardé le contact et expliqué comment arriver. L’auberge est propre, le lit très confortable avec des draps frais. Douche et cuisine sur place. Les espaces en sous-sol permettent de s'abriter en sécurité en cas d'alerte.",
    "rev_REV-04_tag1": "Super Échanges",
    "rev_REV-04_tag2": "Propre & Soigné",
    "rev_REV-04_tag3": "Abri Sécurisé",
    "rev_REV-04_tag4": "Lit Confortable",
    "rev_REV-05_content": "C'est la deuxième fois que je viens et c’était à nouveau un immense plaisir de séjourner ici. Chinju est un hôte fantastique et très professionnel. Ayant eu un retard de vol, je suis arrivé très tard, mais l'accueil tardif s'est fait sans aucun problème. La chambre était propre et la vue était magnifique.",
    "rev_REV-05_tag1": "Client Fidèle",
    "rev_REV-05_tag2": "Hôte Professionnel",
    "rev_REV-05_tag3": "Terrasse Superbe",
    "rev_REV-05_tag4": "Quartier Culturel",
    "rev_REV-06_content": "J'ai adoré cette auberge ! Notamment pour son emplacement parfait, sa jolie vue et son prix imbattable à Kyiv. C'est élégant et simple à la fois. J'avais réservé deux nuits et j'y suis finalement resté deux semaines. On s'y sent tout de suite très bien.",
    "rev_REV-06_tag1": "Séjour Prolongé",
    "rev_REV-06_tag2": "Vue Splendide",
    "rev_REV-06_tag3": "Simple & Élégant",
    "rev_REV-06_tag4": "Tarif Imbattable",
    "rev_REV-07_content": "L'Hotel Cozzydine était l'endroit idéal pour nous détendre et nous reposer après un vol éprouvant. Nous avions d'éxcellentes vues sur la ville, le bâtiment est plein de charme, l'appartement était impeccable et la salle de bain est particulièrement spacieuse. Chinju s'est montré fantastique.",
    "rev_REV-07_tag1": "Vrai Sanctuaire",
    "rev_REV-07_tag2": "Appartement Nickel",
    "rev_REV-07_tag3": "Grande Salle de Bain",
    "rev_REV-07_tag4": "Vues Magnifiques",
    "rev_REV-08_content": "Séjourner dans le superbe Hotel Cozzydine était un véritable conte de fées. Merci mille fois à Chinju pour sa disponibilité 24h/24 et sa superbe hospitalité. Je reviendrai séjourner ici sans aucune hésitation !",
    "rev_REV-08_tag1": "Ambiance Féerique",
    "rev_REV-08_tag2": "Dispo 24/7",
    "rev_REV-08_tag3": "Hôte Exceptionnel",
    "rev_REV-08_tag4": "Jolie Décoration",
    perNight: "/ nuit",
    bedsAvailable: "lits disponibles",
    bedAvailable: "lit disponible",
    longStayDiscount: "Remise long séjour appliquée !",
    lblPrivacyPolicy: "Politique de confidentialité",
    lblAllRightsReserved: "Tous droits réservés.",
    lblPreparingBookingEngine: "Préparation du moteur de réservation...",
    lblLoadingMap: "Chargement de la carte de localisation de Cozzy...",
    lblEmail: "E-mail :",
    lblCallFrontDesk: "Appeler (Réception) :",
    lblHostel: "Auberge",
    lblTransit250m: "250 mètres",
    lblTransit300m: "300 mètres",
    lblTransit3m: "3 min de trajet",
    lblTransit10m: "10 min de trajet",
    lblCozzyAiAssistant: "Cozy AI",
    lblCozzyCoreAi: "Cozy Core AI"
  }
};
